// src/pages/Dashboard.js

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import jsPDF from 'jspdf';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import 'moment/locale/it';
import { AppBar, Toolbar, IconButton, Typography, Container, Paper, Grid, TextField, Button, Checkbox, Avatar, Tooltip, Badge, Table, TableHead, TableRow, TableCell, TableBody, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Select, MenuItem, FormControl, InputLabel, Box, FormControlLabel, Modal, CircularProgress, Link } from '@mui/material';
import { FaBars, FaBook, FaCalendarAlt, FaFacebookF, FaGlobe, FaInstagram, FaPinterestP, FaRedoAlt, FaUndoAlt, FaUserShield, FaWhatsapp } from 'react-icons/fa';
import { RRule } from 'rrule';
import DOMPurify from 'dompurify';
import { db, auth } from '../firebase';
import {
  collection, onSnapshot, deleteDoc, doc, updateDoc, setDoc, getDoc, addDoc, getDocs, query, where, Timestamp, orderBy, limit} from 'firebase/firestore';

import Sidebar from '../components/sidebar/Sidebar';
import StudentForm from '../components/StudentForm';
import StudentEditForm from '../components/StudentEditForm';
import StudentDetails from '../components/StudentDetails';

import '../styles/Dashboard.css';

// Report utils (PDF + Excel)
import {
  generatePdfReport,
  generateMonthlyPdfReport,
  generateStatsPdfReport,
} from '../utils/generatePdfReport';
import { generateExcelStats } from '../utils/generateExcelStats';
import { buildReportNames } from '../utils/reportNaming';

// ==== Storage (SOLO STORAGE) ====
import { uploadFileWithMetadata } from '../utils/storageService';

// ---------- Utils ----------
function openBlobForCatechist(blob, filename, options = {}) {
  const { alsoDownload = true } = options;
  try {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    if (alsoDownload) {
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) {
    console.error('openBlobForCatechist error:', e);
  }
}


const LONG_ABSENCE_THRESHOLD_DAYS = 21;

const PRESENCE_TREND_POINTS = 5;


function createDefaultReadingTurns() {
  return Array.from({ length: 6 }, (_, idx) => ({
    slot: idx + 1,
    studentId: '',
    prayerText: '',
  }));
}
/**
 * Calcola gli studenti assenti da più di LONG_ABSENCE_THRESHOLD_DAYS giorni consecutivi
 * in base ai programmi salvati e alle presenze registrate.
 *
 * @param {Array} students  Elenco studenti
 * @param {Array} programs  Elenco programmi salvati (con meetingDate e presenze)
 * @returns {Array}         Sottoinsieme di students con assenza prolungata
 */
function computeLongAbsenceStudents(students, programs) {
  if (!Array.isArray(students) || !students.length) return [];
  if (!Array.isArray(programs) || !programs.length) return [];

  const programMoments = programs
    .map((p) => moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true))
    .filter((m) => m.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (!programMoments.length) return [];

  const firstProgramDate = programMoments[0];
  const now = moment().startOf('day');

  const lastPresenceMap = new Map();
  programs.forEach((p) => {
    const md = moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
    if (!md.isValid()) return;
    (p.presenze || []).forEach((rec) => {
      if (rec.present) {
        const prev = lastPresenceMap.get(rec.studentId);
        if (!prev || md.isAfter(prev)) {
          lastPresenceMap.set(rec.studentId, md);
        }
      }
    });
  });

  return students.filter((s) => {
    const lastPres = lastPresenceMap.get(s.id) || null;
    if (lastPres) {
      return now.diff(lastPres, 'days') > LONG_ABSENCE_THRESHOLD_DAYS;
    }
    return now.diff(firstProgramDate, 'days') > LONG_ABSENCE_THRESHOLD_DAYS;
  });
}

moment.locale('it');

// Backend Cloud Functions base URL per il recupero dell'agiografia completa
const SANTO_FUNCTIONS_BASE = 'https://us-central1-catechismonline-f295f.cloudfunctions.net/api/api';


function decodeHtmlEntities(str) {
  if (!str) return '';
  if (typeof document === 'undefined') {
    return str
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&agrave;/g, 'à')
      .replace(/&egrave;/g, 'è')
      .replace(/&eacute;/g, 'é')
      .replace(/&igrave;/g, 'ì')
      .replace(/&ograve;/g, 'ò')
      .replace(/&ugrave;/g, 'ù')
      .replace(/&rsquo;/g, '’');
  }
  const txt = document.createElement('textarea');
  txt.innerHTML = str;
  return txt.value;
}

function getAutoLiturgicalOccasion(now = moment()) {

  const year = now.year();

  // Mappa specifica per gli anni 2025 e 2026 (date liturgiche principali)
  const calendarByYear = {
    2025: {
      '2025-11-23': { title: "Nostro Signore Gesù Cristo Re dell'Universo (anno C).", icon: '👑' },
      '2025-11-30': { title: 'I Domenica di Avvento (anno A).', icon: '🕯️' },
      '2025-12-07': { title: 'II Domenica di Avvento (anno A).', icon: '🕯️' },
      '2025-12-14': { title: 'III Domenica di Avvento (anno A).', icon: '🕯️' },
      '2025-12-21': { title: 'IV Domenica di Avvento (anno A).', icon: '🕯️' },
      '2025-12-25': { title: 'Natale del Signore.', icon: '🎄' },
      '2025-12-28': { title: 'Santa Famiglia di Gesù, Maria e Giuseppe (anno A).', icon: '🏠' },
    },
    2026: {
      // Tempo di Natale 2026
      '2026-01-01': { title: 'Maria Santissima Madre di Dio (Solennità).', icon: '🎆' },
      '2026-01-04': { title: 'II Domenica di Natale.', icon: '✨' },
      '2026-01-06': { title: 'Epifania del Signore (Solennità).', icon: '⭐' },
      '2026-01-11': { title: 'Battesimo del Signore.', icon: '💧' },

      // Quaresima 2026 (General C – Italia)
      '2026-02-18': { title: 'Mercoledì delle Ceneri.', icon: '✝️' },
      '2026-02-22': { title: 'I Domenica di Quaresima.', icon: '✝️' },
      '2026-03-01': { title: 'II Domenica di Quaresima.', icon: '✝️' },
      '2026-03-08': { title: 'III Domenica di Quaresima.', icon: '✝️' },
      '2026-03-15': { title: 'IV Domenica di Quaresima «Laetare».', icon: '✝️' },
      '2026-03-22': { title: 'V Domenica di Quaresima.', icon: '✝️' },
      '2026-03-29': { title: 'Domenica delle Palme e della Passione del Signore.', icon: '🌿' },

      // Triduo Pasquale e Tempo di Pasqua 2026
      '2026-04-02': { title: 'Giovedì Santo – Cena del Signore.', icon: '🥖' },
      '2026-04-03': { title: 'Venerdì Santo – Passione del Signore.', icon: '✝️' },
      '2026-04-05': { title: 'Domenica di Pasqua – Risurrezione del Signore.', icon: '🌅' },
      '2026-04-12': { title: 'II Domenica di Pasqua (o della Divina Misericordia).', icon: '🕊️' },
      '2026-04-19': { title: 'III Domenica di Pasqua.', icon: '🕊️' },
      '2026-04-26': { title: 'IV Domenica di Pasqua.', icon: '🕊️' },
      '2026-05-03': { title: 'V Domenica di Pasqua.', icon: '🕊️' },
      '2026-05-10': { title: 'VI Domenica di Pasqua.', icon: '🕊️' },
      '2026-05-14': { title: 'Ascensione del Signore.', icon: '☁️' },
      '2026-05-17': { title: 'VII Domenica di Pasqua.', icon: '🕊️' },
      '2026-05-24': { title: 'Domenica di Pentecoste.', icon: '🔥' },

      // Solennità successive significative
      '2026-05-31': { title: 'Santissima Trinità.', icon: '🔺' },
      '2026-06-07': { title: 'Santissimo Corpo e Sangue di Cristo (Corpus Domini).', icon: '🍞' },
      '2026-06-12': { title: 'Sacratissimo Cuore di Gesù.', icon: '❤️‍🔥' },
    },
  };

  const yearMap = calendarByYear[year];
  if (yearMap) {
    const entries = Object.entries(yearMap)
      .map(([iso, payload]) => ({
        date: moment(iso, 'YYYY-MM-DD'),
        payload,
      }))
      .sort((a, b) => a.date.valueOf() - b.date.valueOf());

    for (let i = 0; i < entries.length; i += 1) {
      const ev = entries[i];
      const startWindow = ev.date.clone().subtract(4, 'days');
      // Finestra: da 4 giorni prima fino al giorno stesso (inclusi)
      if (now.isBetween(startWindow, ev.date, 'day', '[]')) {
        return { ...ev.payload, eventDate: ev.date.clone() };
      }
    }
  }

  // Fallback generico per altri anni: Avvento + Natale + Maria Madre di Dio + Epifania
  const christmas = moment({ year, month: 11, day: 25 });
  const fourthSunday = christmas.clone().subtract(1, 'day').startOf('day');
  while (fourthSunday.isoWeekday() !== 7) {
    fourthSunday.subtract(1, 'day');
  }
  const thirdSunday = fourthSunday.clone().subtract(1, 'week');
  const secondSunday = thirdSunday.clone().subtract(1, 'week');
  const firstSunday = secondSunday.clone().subtract(1, 'week');

  const events = [
    { date: firstSunday, title: 'I Domenica di Avvento', icon: '🕯️' },
    { date: secondSunday, title: 'II Domenica di Avvento', icon: '🕯️' },
    { date: thirdSunday, title: 'III Domenica di Avvento', icon: '🕯️' },
    { date: fourthSunday, title: 'IV Domenica di Avvento', icon: '🕯️' },
    { date: christmas, title: 'Natale del Signore', icon: '🎄' },
    { date: moment({ year: (monthAfter(christmas) === 0 ? year + 1 : year), month: 0, day: 1 }), title: 'Maria SS. Madre di Dio', icon: '🎆' },
    { date: moment({ year: (monthAfter(christmas) === 0 ? year + 1 : year), month: 0, day: 6 }), title: 'Epifania del Signore', icon: '⭐' },
  ];

  function monthAfter(date) {
    return date.clone().add(1, 'month').month();
  }

  for (let i = 0; i < events.length; i += 1) {
    const ev = events[i];
    const startWindow = ev.date.clone().subtract(4, 'days');
    if (now.isBetween(startWindow, ev.date, 'day', '[]')) {
      return { title: ev.title, icon: ev.icon, eventDate: ev.date.clone() };
    }
  }

  return null;
}


function Dashboard() {
  const navigate = useNavigate();

  // =======================
  // STATE PRINCIPALI
  // =======================

  // ==== Privacy ====
  const sanitizeHtml = (html) =>
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'b','strong','i','em','u','p','br','ul','ol','li','a','span','div','h1','h2','h3','h4','h5','h6'
      ],
      ALLOWED_ATTR: ['href','target','rel','style'],
    });

  const isProbablyHtml = (s) => /<\/?[a-z][\s\S]*>/i.test(String(s || ''));
  const enhanceLinks = (safeHtml) =>
    safeHtml.replace(/<a\s+(?![^>]*\btarget=)/gi, '<a target="_blank" rel="noopener noreferrer" ');


  const convertPlainTextToHtml = (text) => {
    const raw = String(text || '').trim();
    if (!raw) return '';
    const paragraphs = raw
      .split(/\n{2,}/)
      .map((p) => p.trim())
      .filter((p) => p);

    const escapedParagraphs = paragraphs.map((p) =>
      p
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
    );

    const html = escapedParagraphs
      .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');

    return sanitizeHtml(html);
  };

  const buildLiturgyHtmlFromSections = (data) => {
    if (!data || !Array.isArray(data.sections) || data.sections.length === 0) {
      return '';
    }

    const escapeHtml = (str) =>
      String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

    const chunks = [];

    if (data.liturgicalColor) {
      chunks.push(
        `<p><em>${escapeHtml(data.liturgicalColor)}</em></p>`
      );
    }

    data.sections.forEach((section) => {
      const label = (section.label || '').trim();
      const rawText = String(section.text || '').trim();

      // Sezione completamente vuota: salta
      if (!label && !rawText) {
        return;
      }

      // Importante: ignoriamo le sezioni senza etichetta (label),
      // perché dal backend arrivano blocchi "tutto attaccato" non desiderati.
      if (!label) {
        return;
      }

      let bodyText = rawText;
      const lowerLabel = label.toLowerCase();
      if (bodyText.toLowerCase().startsWith(lowerLabel)) {
        bodyText = bodyText.slice(label.length).trim();
      }

      // Titolo sezione in grassetto
      chunks.push(`<p><strong>${escapeHtml(label)}</strong></p>`);

      if (bodyText) {
        const bodyHtml = convertPlainTextToHtml(bodyText);
        if (bodyHtml) {
          chunks.push(bodyHtml);
        }
      }

      // Riga vuota tra le sezioni
      chunks.push('<br/>');
    });

    return chunks.join('');
  };
  const [privacyModalOpen, setPrivacyModalOpen] = useState(false);
  const [privacyViewMode, setPrivacyViewMode] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [privacyDoc, setPrivacyDoc] = useState({ text: '', version: '' });
  const [acceptChecked, setAcceptChecked] = useState(false);

  // Stored acceptance (for view mode download)
  const [privacyStoredHash, setPrivacyStoredHash] = useState('');
  const [privacyStoredSignature, setPrivacyStoredSignature] = useState('');
  const [privacyStoredAcceptedAt, setPrivacyStoredAcceptedAt] = useState(null); // dataURL if present
  const [privacyDownloadBusy, setPrivacyDownloadBusy] = useState(false);

  const [user, setUser] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [calendarAlertOpen, setCalendarAlertOpen] = useState(false);
  const [calendarAlertCount, setCalendarAlertCount] = useState(0);
  const [occasionMode, setOccasionMode] = useState('auto'); // 'auto', 'manual' o 'both'
  const [occasionManualTitle, setOccasionManualTitle] = useState('');
  const [occasionManualIcon, setOccasionManualIcon] = useState('');

  const [appbarHighlightConfig, setAppbarHighlightConfig] = useState({
    mode: 'off',
    manualText: '',
    manualIconKey: '',
  });
  const [santoDelGiorno, setSantoDelGiorno] = useState('');
  const [santoImage, setSantoImage] = useState(null);
  const [santoDescription, setSantoDescription] = useState('');
  const [santoTitle, setSantoTitle] = useState('');
  const [santoModalOpen, setSantoModalOpen] = useState(false);
  const [santoLoading, setSantoLoading] = useState(false);
  const [santoError, setSantoError] = useState('');
  const [santoSearchQuery, setSantoSearchQuery] = useState('');
const [, setSantoSearchResults] = useState([]);
const [, setSelectedSantoIndex] = useState(0);
const [santoOfDay, setSantoOfDay] = useState({
  name: '',
  title: '',
  image: null,
  description: '',
  pageUrl: '',
});
const [santoPageUrl, setSantoPageUrl] = useState('');

const santoEffectivePageUrl = useMemo(
  () => santoPageUrl || (santoOfDay && santoOfDay.pageUrl) || '',
  [santoPageUrl, santoOfDay]
);


  




function mapSantoFromRecord(record) {
  if (!record) return null;

  const base =
    record && (record.attributes || record)
      ? (record.attributes || record)
      : {};
  const attrs = base || {};

  const name =
    attrs.nome || attrs.name || attrs.titolo || '';
  const title = attrs.titolo || attrs.title || '';
  const image =
    attrs.immagine ||
    attrs.image ||
    attrs.image_url ||
    attrs.urlimmagine ||
    null;
  const rawDescription =
    attrs.agiografia ||
    attrs.descrizione ||
    attrs.description ||
    attrs.testo ||
    attrs.contenuto ||
    '';
  const pageUrl =
    attrs.urlscheda ||
    attrs.urlScheda ||
    attrs.link ||
    attrs.url ||
    attrs.permalink ||
    attrs.permalink_url ||
    '';

  const description = decodeHtmlEntities(rawDescription || '');

  if (!name && !title && !description && !image && !pageUrl) {
    return null;
  }

  return {
    name,
    title,
    image,
    description,
    pageUrl,
  };
}

function cleanAgiografiaText(raw) {
  if (!raw) return '';
  let text = String(raw).replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  const lines = text
    .split('\n')
    .map((line) => line.trim());

  const footerPatterns = [
    'santodelgiorno.it',
    'santo del giorno',
    'tutti i diritti riservati',
    'privacy',
    'cookie',
    'p.iva',
    'copyright',
  ];

  const kept = lines.filter((line) => line);

  while (kept.length > 0) {
    const last = kept[kept.length - 1];
    const lower = last.toLowerCase();
    if (!last) {
      kept.pop();
      continue;
    }
    if (footerPatterns.some((p) => lower.includes(p))) {
      kept.pop();
      continue;
    }
    break;
  }

  return kept.join('\n\n').trim();
}

async function fetchFullAgiografia(pageUrl) {
  if (!pageUrl) return null;
  try {
    const res = await fetch(
      `${SANTO_FUNCTIONS_BASE}/santo-agiografia?url=${encodeURIComponent(pageUrl)}`
    );
    if (!res.ok) {
      throw new Error(`HTTP ${res.status}`);
    }
    const data = await res.json();
    if (data && data.ok && data.text) {
      return typeof data.text === 'string' ? data.text : String(data.text);
    }
    return null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Errore nel recupero dell'agiografia completa", err);
    return null;
  }
}

// --- Notifica appuntamenti pubblici (una volta per sessione **per utente** e **per giorno**) ---
  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        await fetchPrivacyPolicy();
        const us = await getDoc(doc(db, 'users', user.uid));
        const accepted = !!(us.exists() && us.data().privacyAccepted);
        setPrivacyAccepted(accepted);
        setPrivacyModalOpen(!accepted);
      } catch (e) {
        console.error('privacy init error', e);
        setPrivacyAccepted(false);
        setPrivacyModalOpen(true);
      }
    })();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const todayKey = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const shownKey = `calendarAlertShown:${user.uid}:${todayKey}`;
    if (sessionStorage.getItem(shownKey)) return;

    let unsubscribe;
    try {
      const nowTs = (typeof Timestamp?.now === 'function')
        ? Timestamp.now()
        : { toDate: () => new Date() };
      const nowDate = nowTs.toDate ? nowTs.toDate() : new Date();

      const qy = query(
        collection(db, 'events'),
        where('visibility', '==', 'all'),
        orderBy('start', 'asc')
      );

      unsubscribe = onSnapshot(qy, (snap) => {
        let c = 0;
        snap.forEach((d) => {
          const data = d.data();
          const start = data.start?.toDate ? data.start.toDate() : data.start;
          if (!start) return;
          if (new Date(start) >= nowDate && (data.category || '') !== 'Compleanno') {
            c += 1;
          }
        });

        if (c > 0) {
          setCalendarAlertCount(c);
          setCalendarAlertOpen(true);
          sessionStorage.setItem(shownKey, '1');
        }
      });
    } catch (err) {
      /* ignore */
    }

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [user]);
// Fetch Santo del Giorno
useEffect(() => {
  let isMounted = true;

  const loadSanto = async () => {
    setSantoLoading(true);
    setSantoError('');
    try {
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10);

      const url = `https://www.santodelgiorno.it/santi.json?data=${dateStr}`;

      const res = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const json = await res.json();

      let record = null;

      if (Array.isArray(json?.data)) {
        record = json.data[0];
      } else if (Array.isArray(json)) {
        record = json[0];
      } else if (json && json.data) {
        record = json.data;
      } else {
        record = json;
      }

      if (!record) {
        if (!isMounted) return;
        setSantoDelGiorno('');
        setSantoTitle('');
        setSantoImage(null);
        setSantoDescription('');
        setSantoOfDay({
          name: '',
          title: '',
          image: null,
          description: '',
          pageUrl: '',
        });
        setSantoPageUrl('');
        setSantoSearchResults([]);
        setSelectedSantoIndex(0);
        setSantoError('Impossibile caricare il Santo del giorno.');
        return;
      }

      const detailed = mapSantoFromRecord(record);

      if (!isMounted) return;

      if (!detailed) {
        const fallbackName =
          (record.attributes && record.attributes.nome) || record.nome || '';

        setSantoDelGiorno(fallbackName || '');
        setSantoTitle('');
        setSantoImage(null);
        setSantoDescription('');
        setSantoOfDay({
          name: fallbackName || '',
          title: '',
          image: null,
          description: '',
          pageUrl: '',
        });
        setSantoPageUrl('');
        setSantoSearchResults([]);
        setSelectedSantoIndex(0);
        setSantoError('');
        return;
      }

      let finalDescription = detailed.description || '';
      const finalPageUrl = detailed.pageUrl || '';

      if (finalPageUrl) {
        const full = await fetchFullAgiografia(finalPageUrl);
        if (full && typeof full === 'string' && full.trim()) {
          const cleaned = cleanAgiografiaText(full);
          if (cleaned) {
            finalDescription = cleaned;
          } else {
            finalDescription = full.trim();
          }
        }
      }

      setSantoDelGiorno(detailed.name || '');
      setSantoTitle(detailed.title || '');
      setSantoImage(detailed.image || null);
      setSantoDescription(finalDescription);
      setSantoOfDay({
        name: detailed.name || '',
        title: detailed.title || '',
        image: detailed.image || null,
        description: finalDescription,
        pageUrl: finalPageUrl || '',
      });
      setSantoPageUrl(finalPageUrl || '');
      setSantoSearchResults([]);
      setSelectedSantoIndex(0);
      setSantoError('');
    } catch (error) {
      if (!isMounted) return;
      console.error('Errore nel recupero del Santo del giorno', error);
      setSantoDelGiorno('');
      setSantoTitle('');
      setSantoImage(null);
      setSantoDescription('');
      setSantoOfDay({
        name: '',
        title: '',
        image: null,
        description: '',
        pageUrl: '',
      });
      setSantoPageUrl('');
      setSantoSearchResults([]);
      setSelectedSantoIndex(0);
      setSantoError('Impossibile caricare il Santo del giorno.');
    } finally {
      if (!isMounted) return;
      setSantoLoading(false);
    }
  };

  loadSanto();

  return () => {
    isMounted = false;
  };
}, []);


const handleSantoModalClose = () => {
  setSantoModalOpen(false);
  setSantoError('');
  setSantoSearchQuery('');
  setSantoSearchResults([]);
  setSelectedSantoIndex(0);
  if (
    santoOfDay &&
    (santoOfDay.name ||
      santoOfDay.title ||
      santoOfDay.image ||
      santoOfDay.description)
  ) {
    setSantoDelGiorno(santoOfDay.name || '');
    setSantoTitle(santoOfDay.title || '');
    setSantoImage(santoOfDay.image || null);
    setSantoDescription(santoOfDay.description || '');
    setSantoPageUrl(santoOfDay.pageUrl || '');
  } else {
    setSantoPageUrl('');
  }
};
  const handleSantoSearchSubmit = async (event) => {
    event.preventDefault();
    const query = (santoSearchQuery || '').trim();
    if (!query) {
      return;
    }
  
    setSantoLoading(true);
    setSantoError('');
    try {
  
      const encoded = encodeURIComponent(query);
      const url = `https://www.santodelgiorno.it/santi.json?q=${encoded}`;
  
      const res = await fetch(url, {
        headers: {
          Accept: 'application/json',
        },
      });
  
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
  
      const json = await res.json();
  
      let records = [];
  
      if (Array.isArray(json?.data)) {
        records = json.data;
      } else if (Array.isArray(json)) {
        records = json;
      } else if (json && json.data) {
        records = Array.isArray(json.data) ? json.data : [json.data];
      } else if (json) {
        records = [json];
      }
  
      if (!records.length) {
        setSantoDelGiorno('');
        setSantoTitle('');
        setSantoImage(null);
        setSantoDescription('');
        setSantoSearchResults([]);
        setSelectedSantoIndex(0);
        setSantoError('Nessun santo trovato con questo nome.');
        return;
      }
  
      const detailedResults = [];
  
      for (let i = 0; i < records.length; i += 1) {
        const detailed = mapSantoFromRecord(records[i]);
        if (detailed) {
          detailedResults.push(detailed);
        }
      }
  
      if (!detailedResults.length) {
        const fallbackFirst = records[0];
        const attrs =
          (fallbackFirst && fallbackFirst.attributes) || fallbackFirst || {};

        const fallbackName =
          attrs.nome || attrs.name || attrs.titolo || '';
        setSantoDelGiorno(fallbackName || '');
        setSantoTitle('');
        setSantoImage(null);
        setSantoDescription('');
        setSantoSearchResults([]);
        setSelectedSantoIndex(0);
        return;
      }
  
      setSantoSearchResults(detailedResults);
  
      const first = detailedResults[0];
      setSelectedSantoIndex(0);

      let finalDescription = first.description || '';
      const finalPageUrl = first.pageUrl || '';

      if (finalPageUrl) {
        const full = await fetchFullAgiografia(finalPageUrl);
        if (full && typeof full === 'string' && full.trim()) {
          const cleaned = cleanAgiografiaText(full);
          if (cleaned) {
            finalDescription = cleaned;
          } else {
            finalDescription = full.trim();
          }
        }
      }

      setSantoDelGiorno(first.name || '');
      setSantoTitle(first.title || '');
      setSantoImage(first.image || null);
      setSantoDescription(finalDescription);
      setSantoPageUrl(finalPageUrl || '');
    } catch (error) {
      console.error('Errore nella ricerca del Santo per nome', error);
      setSantoDelGiorno('');
      setSantoTitle('');
      setSantoImage(null);
      setSantoDescription('');
      setSantoSearchResults([]);
      setSelectedSantoIndex(0);
      setSantoError('Impossibile completare la ricerca del Santo.');
    } finally {
      setSantoLoading(false);
    }
  };


  // Open Privacy modal in view-only mode from the top-right button
  const openPrivacyView = async () => {
    try {
      setPrivacyViewMode(true);
      if (user) {
        const uref = doc(db, 'users', user.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const udata = usnap.data() || {};
          setPrivacyStoredHash(udata.privacyHash || '');
          setPrivacyStoredSignature(udata.privacySignature || '');
          try {
            const accAt = udata.privacyAcceptedAt?.toDate
              ? udata.privacyAcceptedAt.toDate()
              : (udata.privacyAcceptedAt || null);
            setPrivacyStoredAcceptedAt(accAt || null);
          } catch (e) {
            /* ignore */
          }
        }
      }
      setPrivacyModalOpen(true);
    } catch (e) {
      console.error(e);
      setPrivacyModalOpen(true);
    }
  };

  const [students, setStudents] = useState([]);
  const [events, setEvents] = useState([]);
  const [meetingDate, setMeetingDate] = useState(''); // YYYY-MM-DD

// Liturgia del giorno
  const [liturgyModalOpen, setLiturgyModalOpen] = useState(false);
  const [liturgyLoading, setLiturgyLoading] = useState(false);
  const [liturgyError, setLiturgyError] = useState('');
  const [liturgyTitle, setLiturgyTitle] = useState('');
  const [liturgySubtitle, setLiturgySubtitle] = useState('');
  const [liturgyContent, setLiturgyContent] = useState('');

  const liturgyDateIso = useMemo(() => {
    // Liturgia del giorno: usiamo SEMPRE la data odierna
    return moment().format('YYYY-MM-DD');
  }, []);

  const handleOpenLiturgyModal = async () => {
    setLiturgyModalOpen(true);
    // Se abbiamo già caricato la liturgia per questa data e non ci sono errori,
    // non ricarichiamo per evitare chiamate inutili.
    if (liturgyContent && !liturgyError) return;

    setLiturgyLoading(true);
    setLiturgyError('');
    try {
      const dateParam = encodeURIComponent(liturgyDateIso);
      // Endpoint backend (Cloud Function):
      // GET ${SANTO_FUNCTIONS_BASE}/liturgia-del-giorno?date=YYYY-MM-DD
      const res = await fetch(
        `${SANTO_FUNCTIONS_BASE}/liturgia-del-giorno?date=${dateParam}`
      );
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const data = await res.json();

      const title = data.title || data.titolo || '';
      const subtitle = data.subtitle || data.sottotitolo || data.tempo || '';

      // HTML strutturato per sezioni (Antifona, Colletta, Letture, Vangelo, ecc.)
      const sectionsHtml = buildLiturgyHtmlFromSections(data);

      // Testo completo in chiaro dal backend
      const plainText = data.text || data.testo || data.contentText || '';

      // Funzione di utilità per togliere eventuali tag
      const stripHtmlTags = (s) => String(s || '').replace(/<[^>]+>/g, '').trim();

      let contentRaw = '';

      // 1) Se il backend ha fornito le sezioni strutturate, usiamo SEMPRE quelle
      //    così otteniamo titoli in grassetto e riga vuota tra le sezioni.
      if (sectionsHtml && stripHtmlTags(sectionsHtml).length > 0) {
        contentRaw = sectionsHtml;
      } else if (plainText) {
        // 2) Fallback principale: usa il testo completo convertito in HTML
        contentRaw = convertPlainTextToHtml(plainText);
      }

      // 3) Ulteriore fallback sull'HTML grezzo, nel caso in cui plainText non sia disponibile.
      if (!contentRaw) {
        contentRaw =
          data.html ||
          data.contentHtml ||
          data.content ||
          data.readingsHtml ||
          data.lettureHtml ||
          '';
      }

      // 4) Ancora un paracadute: eventuale array di letture
      if (!contentRaw && data.readings && Array.isArray(data.readings)) {
        contentRaw = data.readings.join('\n\n');
      }

      const contentSanitized = enhanceLinks(
        sanitizeHtml(
          isProbablyHtml(contentRaw)
            ? contentRaw
            : (contentRaw || '').replace(/\n/g, '<br/>')
        )
      );

      // 5) Pulizia finale: rimuovi "Grandezza Testo A A A" (CEI)
      //    e tutto ciò che segue "Conferenza Episcopale Italiana" (footer CEI).
      let cleanedContent = contentSanitized || '';

// Rimuovi stringhe CEI di interfaccia tipo "Grandezza Testo A A A"
cleanedContent = cleanedContent.replace(/Grandezza[\s\S]{0,80}?Testo[\s\S]{0,80}?A\s*A\s*A/gi, '');

// Taglia via tutto il footer a partire da "Conferenza Episcopale"
cleanedContent = cleanedContent.replace(/Conferenza\s+Episcopale[\s\S]*$/i, '');

// Se nel testo c'è ancora un blocco iniziale "tutto attaccato",
// prova a partire direttamente dalla prima sezione "Antifona".
const idxAntifona = cleanedContent.toLowerCase().indexOf('<strong>antifona');
if (idxAntifona > -1) {
  cleanedContent = cleanedContent.slice(idxAntifona);
}

cleanedContent = cleanedContent.trim();

      setLiturgyTitle(title || 'Liturgia del giorno');
      setLiturgySubtitle(subtitle || '');
      setLiturgyContent(
        cleanedContent || '<p>Testo della liturgia non disponibile.</p>'
      );
    } catch (e) {
      console.error('Errore nel recupero della liturgia del giorno', e);
      setLiturgyTitle('Liturgia del giorno');
      setLiturgySubtitle('');
      setLiturgyContent('');
      setLiturgyError('Impossibile caricare la liturgia del giorno.');
    } finally {
      setLiturgyLoading(false);
    }
  };

  const handleCloseLiturgyModal = () => {
    setLiturgyModalOpen(false);
  };


  const totalStudents = useMemo(() => students.length, [students]);
  const presentStudents = useMemo(
    () => students.filter((s) => !!s.present).length,
    [students]
  );
  const presencePercentage = useMemo(
    () => (totalStudents ? Math.round((presentStudents / totalStudents) * 100) : 0),
    [totalStudents, presentStudents]
  );

  
  const presenceSummaryDateLabel = useMemo(
    () => {
      if (meetingDate) {
        const m = moment(meetingDate);
        if (m.isValid()) {
          return m.format('DD/MM/YYYY');
        }
      }
      return moment().format('DD/MM/YYYY');
    },
    [meetingDate]
  );
const [savedPrograms, setSavedPrograms] = useState([]);
  const [selectedProgramId, setSelectedProgramId] = useState('');

  const [selectedStudent, setSelectedStudent] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // Fields persistenti della Dashboard
  const [dashboardTitle, setDashboardTitle] = useState('Dashboard');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [catechists, setCatechists] = useState('');

  // --- Selezione Anno (cartella per Storage) ---
  const YEAR_OPTIONS = useMemo(() => (
    [
      { value: 'primo',   label: 'Primo anno',   folder: '01-Primo' },
      { value: 'secondo', label: 'Secondo anno', folder: '02-Secondo' },
      { value: 'terzo',   label: 'Terzo anno',   folder: '03-Terzo' },
      { value: 'quarto',  label: 'Quarto anno',  folder: '04-Quarto' },
      { value: 'quinto',  label: 'Quinto anno',  folder: '05-Quinto' },
      { value: 'sesto',   label: 'Sesto anno',   folder: '06-Sesto' },
      { value: 'settimo', label: 'Settimo anno', folder: '07-Settimo' },
    ]
  ), []);
  const [yearLevel, setYearLevel] = useState('');
  const yearFolder = useMemo(() => {
    const found = YEAR_OPTIONS.find(o => o.value === yearLevel);
    return found ? found.folder : null;
  }, [yearLevel, YEAR_OPTIONS]);

  // Etichetta anno per nomi file/PDF (uso la cartella numerata es. "01-Primo")
  const getYearLabel = useCallback(() => {
    const found = YEAR_OPTIONS.find(o => o.value === yearLevel);
    return found ? found.folder : '';
  }, [YEAR_OPTIONS, yearLevel]);

  const [lessonPlan, setLessonPlan] = useState('');

  // Notifiche/Badge
  const [upcomingEventsCount, setUpcomingEventsCount] = useState(0);
  const [birthdayNotifications, setBirthdayNotifications] = useState(0);

  // Dialog gestione programmi
  const [openSaveDialog, setOpenSaveDialog] = useState(false);
  const [openDeleteProgramDialog, setOpenDeleteProgramDialog] = useState(false);
  const [openConflictDialog, setOpenConflictDialog] = useState(false);
  const [conflictProgram, setConflictProgram] = useState(null);
  const [openRenameDialog, setOpenRenameDialog] = useState(false);
  const [newMeetingDate, setNewMeetingDate] = useState('');
  const [openResetDialog, setOpenResetDialog] = useState(false);

  // Report dialogs
  const [openGeneratePdfDialog, setOpenGeneratePdfDialog] = useState(false);
  const [openMonthlyReportDialog, setOpenMonthlyReportDialog] = useState(false);
  const [selectedReportMonth, setSelectedReportMonth] = useState(moment().month() + 1); // 1-12
  const [selectedReportYear, setSelectedReportYear] = useState(moment().year());
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [openStatsOverview, setOpenStatsOverview] = useState(false);
  const [asOfDate, setAsOfDate] = useState(moment().format('YYYY-MM-DD'));


  // Cruscotto presenze: selezione giorno e andamento
  const [selectedOverviewProgramId, setSelectedOverviewProgramId] = useState('');

  const presenceTrend = useMemo(() => {
    if (!Array.isArray(savedPrograms) || !savedPrograms.length) return [];
    return savedPrograms
      .filter((p) => Array.isArray(p.presenze) && p.presenze.length && p.meetingDate)
      .map((p) => {
        const total = p.presenze.length;
        const present = p.presenze.filter((rec) => !!rec.present).length;
        const percentage = total ? Math.round((present / total) * 100) : 0;
        const m = moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        return {
          id: p.id,
          meetingDate: p.meetingDate,
          label: m.isValid() ? m.format('DD/MM') : (p.meetingDate || ''),
          percentage,
        };
      })
      .sort((a, b) => {
        const ma = moment(a.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        const mb = moment(b.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        return ma.valueOf() - mb.valueOf();
      });
  }, [savedPrograms]);

  const presenceTrendLast = useMemo(
    () => (presenceTrend.length > 0
      ? presenceTrend.slice(-Math.min(PRESENCE_TREND_POINTS, presenceTrend.length))
      : []),
    [presenceTrend]
  );

  const trendAverage = useMemo(() => {
    if (!presenceTrendLast.length) return 0;
    const sum = presenceTrendLast.reduce((acc, cur) => acc + (cur.percentage || 0), 0);
    return Math.round(sum / presenceTrendLast.length);
  }, [presenceTrendLast]);

  const trendDirectionLabel = useMemo(() => {
    if (presenceTrendLast.length < 2) return 'Andamento non disponibile';
    const first = presenceTrendLast[0].percentage || 0;
    const last = presenceTrendLast[presenceTrendLast.length - 1].percentage || 0;
    const delta = last - first;
    if (Math.abs(delta) <= 3) return 'Andamento stabile';
    if (delta > 0) return 'Presenze in crescita';
    return 'Presenze in calo';
  }, [presenceTrendLast]);

  const bestDay = useMemo(() => {
    if (!presenceTrend.length) return null;
    return presenceTrend.reduce((best, cur) => {
      if (!best) return cur;
      return cur.percentage > best.percentage ? cur : best;
    }, null);
  }, [presenceTrend]);

  const worstDay = useMemo(() => {
    if (!presenceTrend.length) return null;
    return presenceTrend.reduce((worst, cur) => {
      if (!worst) return cur;
      return cur.percentage < worst.percentage ? cur : worst;
    }, null);
  }, [presenceTrend]);

  // Programma selezionato nel cruscotto (default: data odierna, altrimenti ultimo)
  useEffect(() => {
    if (!savedPrograms.length) {
      if (selectedOverviewProgramId) {
        setSelectedOverviewProgramId('');
      }
      return;
    }
    if (selectedOverviewProgramId && savedPrograms.some((p) => p.id === selectedOverviewProgramId)) {
      return;
    }
    let defaultProgram = null;
    if (meetingDate) {
      defaultProgram = savedPrograms.find((p) => p.meetingDate === meetingDate) || null;
    }
    if (!defaultProgram) {
      const todayStr = moment().format('YYYY-MM-DD');
      defaultProgram = savedPrograms.find((p) => p.meetingDate === todayStr) || null;
    }
    if (!defaultProgram) {
      const sorted = [...savedPrograms].sort((a, b) => {
        const da = a.meetingDate ? new Date(a.meetingDate) : new Date(0);
        const dbb = b.meetingDate ? new Date(b.meetingDate) : new Date(0);
        return dbb - da;
      });
      defaultProgram = sorted[0];
    }
    if (defaultProgram && defaultProgram.id !== selectedOverviewProgramId) {
      setSelectedOverviewProgramId(defaultProgram.id);
    }
  }, [savedPrograms, selectedOverviewProgramId, meetingDate]);

  const overviewProgram = useMemo(
    () => (selectedOverviewProgramId
      ? savedPrograms.find((p) => p.id === selectedOverviewProgramId) || null
      : null),
    [savedPrograms, selectedOverviewProgramId]
  );

  const overviewTotalStudents = useMemo(
    () => (overviewProgram && Array.isArray(overviewProgram.presenze)
      ? overviewProgram.presenze.length
      : 0),
    [overviewProgram]
  );

  const overviewPresentStudents = useMemo(
    () => (overviewProgram && Array.isArray(overviewProgram.presenze)
      ? overviewProgram.presenze.filter((rec) => !!rec.present).length
      : 0),
    [overviewProgram]
  );

  const overviewAbsentStudents = useMemo(
    () => Math.max(overviewTotalStudents - overviewPresentStudents, 0),
    [overviewTotalStudents, overviewPresentStudents]
  );

  const overviewPresencePercentage = useMemo(
    () => (overviewTotalStudents
      ? Math.round((overviewPresentStudents / overviewTotalStudents) * 100)
      : 0),
    [overviewTotalStudents, overviewPresentStudents]
  );

  const overviewAbsencePercentage = useMemo(
    () => (overviewTotalStudents ? 100 - overviewPresencePercentage : 0),
    [overviewTotalStudents, overviewPresencePercentage]
  );
  
  // Strumenti rapidi per il catechista
  const [toolsModalOpen, setToolsModalOpen] = useState(false);
  const [randomStudentResult, setRandomStudentResult] = useState(null);
  const [groupsCount, setGroupsCount] = useState(2);
  const [generatedGroups, setGeneratedGroups] = useState([]);
  const [includeOnlyPresentForTools, setIncludeOnlyPresentForTools] = useState(true);

  const [readingTurns, setReadingTurns] = useState(createDefaultReadingTurns());
  const [readingTurnsLoading, setReadingTurnsLoading] = useState(false);
  const [readingTurnsSaving, setReadingTurnsSaving] = useState(false);
  const [readingTurnsLog, setReadingTurnsLog] = useState([]);
  const [readingTurnsLogLoading, setReadingTurnsLogLoading] = useState(false);


  const toolStudents = useMemo(() => {
    let list = Array.isArray(students) ? students : [];
    if (includeOnlyPresentForTools) {
      list = list.filter((s) => !!s.present);
    }
    return list;
  }, [students, includeOnlyPresentForTools]);

  const handlePickRandomStudent = useCallback(() => {
    if (!toolStudents.length) {
      setRandomStudentResult(null);
      notify('info', 'Nessuno studente disponibile per la selezione casuale.');
      return;
    }
    const index = Math.floor(Math.random() * toolStudents.length);
    const s = toolStudents[index];
    setRandomStudentResult(s);
  }, [toolStudents]);

  const handleGenerateGroups = useCallback(() => {
    const n = Number(groupsCount) || 0;
    if (n < 2) {
      notify('warning', 'Inserisci almeno 2 gruppi.');
      return;
    }
    if (!toolStudents.length) {
      setGeneratedGroups([]);
      notify('info', 'Nessuno studente disponibile per creare i gruppi.');
      return;
    }
    const shuffled = [...toolStudents];
    for (let i = shuffled.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = shuffled[i];
      shuffled[i] = shuffled[j];
      shuffled[j] = tmp;
    }
    const groups = Array.from({ length: n }, () => []);
    shuffled.forEach((s, idx) => {
      groups[idx % n].push(s);
    });
    setGeneratedGroups(groups);
  }, [toolStudents, groupsCount]);

  useEffect(() => {
    if (!user || !meetingDate) {
      setReadingTurns(createDefaultReadingTurns());
      return;
    }
    let isActive = true;
    const load = async () => {
      try {
        setReadingTurnsLoading(true);
        const rtRef = doc(db, 'readingTurns', `${user.uid}_${meetingDate}`);
        const snap = await getDoc(rtRef);
        if (!isActive) return;
        if (snap.exists()) {
          const data = snap.data();
          if (Array.isArray(data.turns) && data.turns.length) {
            const base = createDefaultReadingTurns();
            const merged = base.map((slot) => {
              const found = data.turns.find((t) => t.slot === slot.slot);
              return found
                ? {
                  slot: slot.slot,
                  studentId: found.studentId || '',
                  prayerText: found.prayerText || '',
                }
                : slot;
            });
            setReadingTurns(merged);
          } else {
            setReadingTurns(createDefaultReadingTurns());
          }
        } else {
          setReadingTurns(createDefaultReadingTurns());
        }
      } catch (e) {
        console.error('Errore caricamento turni lettura:', e);
        notify('error', 'Errore durante il caricamento dei turni di lettura.');
        setReadingTurns(createDefaultReadingTurns());
      } finally {
        if (isActive) {
          setReadingTurnsLoading(false);
        }
      }
    };
    load();
    return () => {
      isActive = false;
    };
  }, [user, meetingDate]);

  useEffect(() => {
    if (!user || !toolsModalOpen) {
      return;
    }
    let isActive = true;
    const loadLog = async () => {
      try {
        setReadingTurnsLogLoading(true);
        const logCol = collection(db, 'readingTurnsLog');
        const qy = query(
          logCol,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          limit(20)
        );
        const snap = await getDocs(qy);
        if (!isActive) return;
        const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setReadingTurnsLog(items);
      } catch (e) {
        console.error('Errore caricamento registro letture:', e);
      } finally {
        if (isActive) {
          setReadingTurnsLogLoading(false);
        }
      }
    };
    loadLog();
    return () => {
      isActive = false;
    };
  }, [user, toolsModalOpen]);

  const handleChangeReadingTurnStudent = useCallback((slot, studentId) => {
    setReadingTurns((prev) => prev.map((row) => (
      row.slot === slot ? { ...row, studentId } : row
    )));
  }, []);

  const handleChangeReadingTurnPrayer = useCallback((slot, prayerText) => {
    setReadingTurns((prev) => prev.map((row) => (
      row.slot === slot ? { ...row, prayerText } : row
    )));
  }, []);

  const handleSaveReadingTurns = useCallback(async () => {
    if (!user || !meetingDate) {
      notify('warning', 'Imposta la data dell\'incontro prima di salvare i turni di lettura.');
      return;
    }
    try {
      setReadingTurnsSaving(true);
      const rtRef = doc(db, 'readingTurns', `${user.uid}_${meetingDate}`);
      const payload = {
        userId: user.uid,
        meetingDate,
        yearLevel: yearLevel || null,
        turns: readingTurns.map((row) => ({
          slot: row.slot,
          studentId: row.studentId || '',
          prayerText: row.prayerText || '',
        })),
      };
      await setDoc(rtRef, payload, { merge: true });

      const effectiveTurns = readingTurns.filter((row) => row.studentId || row.prayerText);
      if (effectiveTurns.length) {
        const logCol = collection(db, 'readingTurnsLog');
        const now = new Date();
        const logWrites = effectiveTurns.map((row) => {
          const student = Array.isArray(students)
            ? students.find((s) => s.id === row.studentId)
            : null;
          const displayName = student
            ? `${student.cognome || ''} ${student.nome || ''}`.trim()
            : '';
          const logRef = doc(logCol);
          return setDoc(logRef, {
            userId: user.uid,
            meetingDate: meetingDate || null,
            slot: row.slot,
            studentId: row.studentId || null,
            studentName: displayName || null,
            prayerText: row.prayerText || '',
            createdAt: Timestamp.now(),
          });
        });
        await Promise.all(logWrites);

        const localEntries = effectiveTurns.map((row) => {
          const student = Array.isArray(students)
            ? students.find((s) => s.id === row.studentId)
            : null;
          const displayName = student
            ? `${student.cognome || ''} ${student.nome || ''}`.trim()
            : '';
          return {
            id: `${meetingDate || 'nd'}_${row.slot}_${now.getTime()}`,
            userId: user.uid,
            meetingDate: meetingDate || null,
            slot: row.slot,
            studentId: row.studentId || null,
            studentName: displayName || null,
            prayerText: row.prayerText || '',
            createdAt: now,
          };
        });
        setReadingTurnsLog((prev) => [...localEntries, ...(prev || [])].slice(0, 20));
      }

      notify('success', 'Turni di lettura salvati con successo.');
    } catch (e) {
      console.error('Errore salvataggio turni lettura:', e);
      notify('error', 'Errore durante il salvataggio dei turni di lettura.');
    } finally {
      setReadingTurnsSaving(false);
    }
  }, [user, meetingDate, yearLevel, readingTurns, students]);

  const handleExportReadingTurnsPdf = useCallback(() => {
    if (!readingTurns || !readingTurns.length) {
      notify('info', 'Non ci sono turni di lettura da esportare.');
      return;
    }
    const effectiveTurns = readingTurns.filter((row) => row.studentId || row.prayerText);
    if (!effectiveTurns.length) {
      notify('info', 'Compila almeno una preghiera prima di esportare il PDF.');
      return;
    }
    const docPdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const pageHeight = docPdf.internal.pageSize.getHeight();
    let y = margin;

    docPdf.setFont('Helvetica', 'bold');
    docPdf.setFontSize(16);
    docPdf.text('Turni di lettura delle preghiere dei fedeli', pageWidth / 2, y, { align: 'center' });
    y += 26;

    docPdf.setFont('Helvetica', 'normal');
    docPdf.setFontSize(11);
    const dateLabel = meetingDate
      ? moment(meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true).isValid()
        ? moment(meetingDate).format('DD/MM/YYYY')
        : meetingDate
      : '';
    if (dateLabel) {
      docPdf.text(`Data: ${dateLabel}`, margin, y);
      y += 18;
    }

    const colSlotX = margin;
    const colNameX = margin + 40;
    const colPrayerX = margin + 220;
    const maxWidthPrayer = pageWidth - margin - colPrayerX;

    docPdf.setFont('Helvetica', 'bold');
    docPdf.text('#', colSlotX, y);
    docPdf.text('Ragazzo', colNameX, y);
    docPdf.text('Preghiera', colPrayerX, y);
    y += 16;
    docPdf.setFont('Helvetica', 'normal');

    effectiveTurns.forEach((row) => {
      if (y > pageHeight - margin) {
        docPdf.addPage();
        y = margin;

        docPdf.setFont('Helvetica', 'bold');
        docPdf.setFontSize(11);
        docPdf.text('#', colSlotX, y);
        docPdf.text('Ragazzo', colNameX, y);
        docPdf.text('Preghiera', colPrayerX, y);
        y += 16;
        docPdf.setFont('Helvetica', 'normal');
      }

      const student = Array.isArray(students)
        ? students.find((s) => s.id === row.studentId)
        : null;
      const studentName = student ? `${student.cognome} ${student.nome}` : '';

      const wrappedPrayer = row.prayerText
        ? docPdf.splitTextToSize(row.prayerText, maxWidthPrayer)
        : [''];

      docPdf.text(String(row.slot), colSlotX, y);
      docPdf.text(studentName || '-', colNameX, y);
      wrappedPrayer.forEach((line, idx) => {
        const lineY = y + idx * 14;
        if (lineY > pageHeight - margin) {
          docPdf.addPage();
          y = margin;
        }
        docPdf.text(line, colPrayerX, lineY);
      });
      y += Math.max(wrappedPrayer.length * 14, 16);
    });

    const safeDate = meetingDate || 'senza_data';
    docPdf.save(`Turni_pregiere_${safeDate}.pdf`);
    notify('success', 'PDF dei turni di lettura generato.');
  }, [readingTurns, students, meetingDate]);
// Conferma invio (unificata)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPayload, setConfirmPayload] = useState({ type: null, title: '', files: [] });

  // Dialog upload (unificata) + snackbar
  const [uploadDlgOpen, setUploadDlgOpen] = useState(false);
  const [uploadDlgTitle, setUploadDlgTitle] = useState('Caricamento report');
  const [uploadDlgMsg, setUploadDlgMsg] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [snack, setSnack] = useState({ open: false, type: 'info', msg: '' });
  const [weeklyReminderOpen, setWeeklyReminderOpen] = useState(false);
  const [lastResetSnapshot, setLastResetSnapshot] = useState(null);
  const [canRestoreReset, setCanRestoreReset] = useState(false);

// Alert assenti oltre 3 settimane
const [absentAlertOpen, setAbsentAlertOpen] = useState(false);
const [longAbsenceStudents, setLongAbsenceStudents] = useState([]);

  // Reminder domenicale
  useEffect(() => {
    const today = moment();
    const isSunday = today.isoWeekday() === 7;
    const key = `weeklyReminderDismissed-${today.format('YYYYMMDD')}`;
    if (isSunday && !localStorage.getItem(key)) {
      setWeeklyReminderOpen(true);
    }
  }, []);

  // =======================
  // HELPERS
  // =======================
  function notify(type, msg) {
    setSnack({ open: true, type, msg });
  }
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const toggleSidebar = () => setIsSidebarOpen((prev) => !prev);

  const currentDateFormatted = useMemo(() => moment().format('dddd, D MMMM YYYY'), []);
  const currentOccasion = useMemo(() => {
    const now = moment();
    const autoOccasion = getAutoLiturgicalOccasion(now);

    const manualOccasion = occasionManualTitle
      ? { title: occasionManualTitle, icon: occasionManualIcon || '⭐' }
      : null;

    if (occasionMode === 'manual' && manualOccasion) {
      return manualOccasion;
    }

    if (occasionMode === 'both') {
      if (manualOccasion && autoOccasion) {
        return {
          title: manualOccasion.title,
          icon: manualOccasion.icon,
        };
      }
      return manualOccasion || autoOccasion || null;
    }

    // default: 'auto'
    return autoOccasion || manualOccasion || null;
  }, [occasionMode, occasionManualTitle, occasionManualIcon]);
  useEffect(() => {
    const ref = doc(db, 'settings', 'appbarHighlight');
    const unsubscribe = onSnapshot(
      ref,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() || {};
          const cfg = data.config || {};
          setAppbarHighlightConfig({
            mode: cfg.mode || 'off',
            manualText: (cfg.manualText || '').trim(),
            manualIconKey: (cfg.manualIconKey || '').trim(),
          });
        } else {
          setAppbarHighlightConfig({
            mode: 'off',
            manualText: '',
            manualIconKey: '',
          });
        }
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Errore onSnapshot appbarHighlight:', error);
      }
    );

    return () => unsubscribe();
  }, []);

  
  const APPBAR_MANUAL_ICON_EMOJI = useMemo(() => ({
  open: '🔗',
  newspaper: '📰',
  flyer: '📄',
  note: '📝',
  policy: '📃',
  cloud: '☁️',
  folder: '📁',
  howToReg: '✅',
  fix: '🛠️',
  newFolder: '📁',
  event: '📅',
  today: '📆',
  favorite: '❤️',
  info: 'ℹ️',
  help: '❓',
  settings: '⚙️',
  assessment: '📊',
  checklist: '✅',
  checkCircle: '✅',
  calendarMonth: '🗓️',
  volunteer: '🤝',
  library: '📚',
  offer: '🏷️',
  florist: '🌸',
  night: '🌙',
  star: '⭐',
  bell: '🔔',
  warning: '⚠️',
  check: '✅',
  heart: '❤️',
  heart_blue: '💙',
  heart_green: '💚',
  heart_yellow: '💛',
  heart_purple: '💜',
  star2: '✨',
  sparkles: '🌟',
  fire: '🔥',
  clap: '👏',
  pray: '🙏',
  hands_up: '🙌',
  smile: '😊',
  smile_halo: '😇',
  smile_heart: '🥰',
  party: '🥳',
  tada: '🎉',
  balloon: '🎈',
  gift: '🎁',
  cross: '✝️',
  church: '⛪',
  dove: '🕊️',
  candle: '🕯️',
  bible: '📖',
  rosary: '📿',
  chalice: '🍷',
  bread: '🍞',
  fish: '🐟',
  angel: '👼',
  sheep: '🐑',
  heart_cross: '❤️‍🔥',
  calendar: '📅',
  calendar_spiral: '🗓️',
  clock: '🕒',
  alarm: '⏰',
  hourglass: '⌛',
  stopwatch: '⏱️',
  sunrise: '🌅',
  sunset: '🌇',
  sun: '☀️',
  moon: '🌙',
  stars: '🌌',
  school: '🏫',
  backpack: '🎒',
  book: '📘',
  books: '📚',
  pencil: '✏️',
  pen: '🖊️',
  crayons: '🖍️',
  ruler: '📏',
  notebook: '📓',
  teacher: '👩‍🏫',
  students: '🧑‍🎓',
  kids: '🧒',
  family: '👨‍👩‍👧‍👦',
  group: '👥',
  megaphone: '📣',
  email: '✉️',
  phone: '☎️',
  meeting: '🗣️',
  handshake: '🤝',
  coffee: '☕',
  tea: '🍵',
  lunch: '🍽️',
  cake: '🍰',
  pizza: '🍕',
  hands_heart: '🫶',
  giving: '🤲',
  donate: '💝',
  box_food: '📦',
  shopping_cart: '🛒',
  leaf: '🍃',
  four_leaves: '🍀',
  flower: '🌸',
  rose: '🌹',
  sunflower: '🌻',
  snowflake: '❄️',
  snowman: '☃️',
  tree: '🌳',
  christmas_tree: '🎄',
  seedling: '🌱',
  idea: '💡',
  target: '🎯',
  pin: '📌',
  location: '📍',
  map: '🗺️',
  lock: '🔒',
  unlock: '🔓',
  key: '🔑',
  star_circle: '⭕',
  wow: '😮',
  thinking: '🤔',
  relief: '😌',
  cool: '😎',
  cry: '😢',
  cry_joy: '😂',
  grateful: '🤗',
  generic_001: '🔹',
  generic_002: '🔸',
  generic_003: '🔷',
  generic_004: '🔶',
  generic_005: '⬜',
  generic_006: '⬛',
  generic_007: '⚪',
  generic_008: '⚫',
  generic_009: '🟢',
  generic_010: '🔵',
  generic_011: '🔹',
  generic_012: '🔸',
  generic_013: '🔷',
  generic_014: '🔶',
  generic_015: '⬜',
  generic_016: '⬛',
  generic_017: '⚪',
  generic_018: '⚫',
  generic_019: '🟢',
  generic_020: '🔵',
  generic_021: '🔹',
  generic_022: '🔸',
  generic_023: '🔷',
  generic_024: '🔶',
  generic_025: '⬜',
  generic_026: '⬛',
  generic_027: '⚪',
  generic_028: '⚫',
  generic_029: '🟢',
  generic_030: '🔵',
  generic_031: '🔹',
  generic_032: '🔸',
  generic_033: '🔷',
  generic_034: '🔶',
  generic_035: '⬜',
  generic_036: '⬛',
  generic_037: '⚪',
  generic_038: '⚫',
  generic_039: '🟢',
  generic_040: '🔵',
  generic_041: '🔹',
  generic_042: '🔸',
  generic_043: '🔷',
  generic_044: '🔶',
  generic_045: '⬜',
  generic_046: '⬛',
  generic_047: '⚪',
  generic_048: '⚫',
  generic_049: '🟢',
  generic_050: '🔵',
  generic_051: '🔹',
  generic_052: '🔸',
  generic_053: '🔷',
  generic_054: '🔶',
  generic_055: '⬜',
  generic_056: '⬛',
  generic_057: '⚪',
  generic_058: '⚫',
  generic_059: '🟢',
  generic_060: '🔵',
  generic_061: '🔹',
  generic_062: '🔸',
  generic_063: '🔷',
  generic_064: '🔶',
  generic_065: '⬜',
  generic_066: '⬛',
  generic_067: '⚪',
  generic_068: '⚫',
  generic_069: '🟢',
  generic_070: '🔵',
  generic_071: '🔹',
  generic_072: '🔸',
  generic_073: '🔷',
  generic_074: '🔶',
  generic_075: '⬜',
  generic_076: '⬛',
  generic_077: '⚪',
  generic_078: '⚫',
  generic_079: '🟢',
  generic_080: '🔵',
  generic_081: '🔹',
  generic_082: '🔸',
  generic_083: '🔷',
  generic_084: '🔶',
  generic_085: '⬜',
  generic_086: '⬛',
  generic_087: '⚪',
  generic_088: '⚫',
  generic_089: '🟢',
  generic_090: '🔵',
  generic_091: '🔹',
  generic_092: '🔸',
  generic_093: '🔷',
  generic_094: '🔶',
  generic_095: '⬜',
  generic_096: '⬛',
  generic_097: '⚪',
  generic_098: '⚫',
  generic_099: '🟢',
  generic_100: '🔵',
  generic_101: '🔹',
  generic_102: '🔸',
  generic_103: '🔷',
  generic_104: '🔶',
  generic_105: '⬜',
  generic_106: '⬛',
  generic_107: '⚪',
  generic_108: '⚫',
  generic_109: '🟢',
  generic_110: '🔵',
  generic_111: '🔹',
  generic_112: '🔸',
  generic_113: '🔷',
  generic_114: '🔶',
  generic_115: '⬜',
  generic_116: '⬛',
  generic_117: '⚪',
  generic_118: '⚫',
  generic_119: '🟢',
  generic_120: '🔵',
  generic_121: '🔹',
  generic_122: '🔸',
  generic_123: '🔷',
  generic_124: '🔶',
  generic_125: '⬜',
  generic_126: '⬛',
  generic_127: '⚪',
  generic_128: '⚫',
  generic_129: '🟢',
  generic_130: '🔵',
  generic_131: '🔹',
  generic_132: '🔸',
  generic_133: '🔷',
  generic_134: '🔶',
  generic_135: '⬜',
  generic_136: '⬛',
  generic_137: '⚪',
  generic_138: '⚫',
  generic_139: '🟢',
  generic_140: '🔵',
  generic_141: '🔹',
  generic_142: '🔸',
  generic_143: '🔷',
  generic_144: '🔶',
  generic_145: '⬜',
  generic_146: '⬛',
  generic_147: '⚪',
  generic_148: '⚫',
  generic_149: '🟢',
  generic_150: '🔵',
  generic_151: '🔹',
  generic_152: '🔸',
  generic_153: '🔷',
  generic_154: '🔶',
  generic_155: '⬜',
  generic_156: '⬛',
  generic_157: '⚪',
  generic_158: '⚫',
  generic_159: '🟢',
  generic_160: '🔵',
  generic_161: '🔹',
  generic_162: '🔸',
  generic_163: '🔷',
  generic_164: '🔶',
  generic_165: '⬜',
  generic_166: '⬛',
  generic_167: '⚪',
  generic_168: '⚫',
  generic_169: '🟢',
  generic_170: '🔵',
  generic_171: '🔹',
  generic_172: '🔸',
  generic_173: '🔷',
  generic_174: '🔶',
  generic_175: '⬜',
  generic_176: '⬛',
  generic_177: '⚪',
  generic_178: '⚫',
  generic_179: '🟢',
  generic_180: '🔵',
  generic_181: '🔹',
  generic_182: '🔸',
  generic_183: '🔷',
  generic_184: '🔶',
  generic_185: '⬜',
  generic_186: '⬛',
  generic_187: '⚪',
  generic_188: '⚫',
  generic_189: '🟢',
  generic_190: '🔵',
  generic_191: '🔹',
  generic_192: '🔸',
  generic_193: '🔷',
  generic_194: '🔶',
  generic_195: '⬜',
  generic_196: '⬛',
  generic_197: '⚪',
  generic_198: '⚫',
  generic_199: '🟢',
  generic_200: '🔵',
}), []);
const appbarHighlight = useMemo(() => {
    const mode = appbarHighlightConfig.mode || 'off';
    const manualText = (appbarHighlightConfig.manualText || '').trim();
    const manualIconKey = (appbarHighlightConfig.manualIconKey || '').trim();

    if (mode === 'off') {
      return { text: '', icon: '' };
    }

    let text = '';
    let icon = '';

    if ((mode === 'auto' || mode === 'both') && currentOccasion && currentOccasion.title) {
      text = currentOccasion.title;
      icon = currentOccasion.icon || '✨';
    }

    if ((mode === 'manual' || mode === 'both') && manualText) {
      if (text) {
        text = `${manualText} · ${text}`;
      } else {
        text = manualText;
      }
      icon =
        (manualIconKey && APPBAR_MANUAL_ICON_EMOJI[manualIconKey]) ||
        APPBAR_MANUAL_ICON_EMOJI.star ||
        '📝';
    }

    return { text, icon };
  }, [appbarHighlightConfig, currentOccasion, APPBAR_MANUAL_ICON_EMOJI]);

  const { text: appbarHighlightText, icon: appbarHighlightIcon } = appbarHighlight;



  const appbarHighlightDateLabel = useMemo(() => {
    if (currentOccasion && currentOccasion.eventDate) {
      const m = moment(currentOccasion.eventDate);
      if (m.isValid()) {
        return m.format('DD/MM/YYYY');
      }
    }
    return moment().format('DD/MM/YYYY');
  }, [currentOccasion]);

    // Estrae data incontro da un nome (pattern DD_MM_YYYY)
  const extractMeetingDateFromName = (name) => {
    if (!name) return null;
    const m = name.match(/(\b\d{2})_(\d{2})_(\d{4})\b/);
    if (!m) return null;
    const [ , dd, mm, yyyy ] = m;
    const d = moment(`${yyyy}-${mm}-${dd}`, 'YYYY-MM-DD', true);
    return d.isValid() ? d.toDate() : null;
  };

  const showUploadDialog = (title, msg = '') => {
    setUploadDlgTitle(title || 'Caricamento report');
    setUploadDlgMsg(msg || '');
    setUploadPct(0);
    setUploadDlgOpen(true);
  };
  const closeUploadDialog = () => setUploadDlgOpen(false);

  const askConfirm = (payload) => {
    setConfirmPayload(payload || { type: null });
    setConfirmOpen(true);
  };
  const closeConfirm = () => setConfirmOpen(false);

  // =======================
  // AUTH + PERSISTENZA
  // =======================
  useEffect(() => {
    let unsubscribeEvents;
    const unsubAuth = auth.onAuthStateChanged(async (u) => {
      if (!u) {
        navigate('/login');
        return;
      }
      setUser(u);
      fetchPersistentData(u.uid);
      unsubscribeEvents = loadEvents(u.uid);
    });
    return () => {
      unsubAuth();
      if (unsubscribeEvents) unsubscribeEvents();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchPersistentData = async (userId) => {
    try {
      const docRef = doc(db, 'users', userId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.dashboardTitle) setDashboardTitle(data.dashboardTitle);
        if (data.catechists) setCatechists(data.catechists);
        if (data.lessonPlan) setLessonPlan(data.lessonPlan);
        if (data.meetingDate) setMeetingDate(data.meetingDate);
        if (data.yearLevel) setYearLevel(data.yearLevel);
        if (data.occasionMode) setOccasionMode(data.occasionMode);
        if (data.occasionManualTitle) setOccasionManualTitle(data.occasionManualTitle);
        if (data.occasionManualIcon) setOccasionManualIcon(data.occasionManualIcon);
      }
    } catch (e) {
      console.error('Errore fetchPersistentData:', e);
    }
  };

  const savePersistentData = async (userId, data) => {
    try {
      const ref = doc(db, 'users', userId);
      await setDoc(ref, data, { merge: true });
    } catch (e) {
      console.error('Errore savePersistentData:', e);
    }
  };

  // =======================
  // STUDENTI
  // =======================
  useEffect(() => {
    if (!user) return;
    const studentsCol = collection(db, 'students');
    const qy = query(studentsCol, where('userId', '==', user.uid));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.cognome || '').localeCompare(b.cognome || ''));
      setStudents(list);
    });
    return () => unsub();
  }, [user]);

  const toggleAttendance = async (student) => {
    try {
      await updateDoc(doc(db, 'students', student.id), { present: !student.present });
    } catch (e) {
      console.error('Errore toggleAttendance:', e);
      notify('error', "Errore durante l'aggiornamento della presenza.");
    }
  };

  // =======================
  // EVENTI (Compleanni + notifiche)
  // =======================
  const loadEvents = useCallback((userId) => {
    const eventsCol = collection(db, 'events');
    const qy = query(eventsCol, where('userId', '==', userId));
    const unsub = onSnapshot(qy, (snap) => {
      const data = snap.docs.map((d) => {
        const s = d.data();
        return {
          id: d.id,
          ...s,
          start: s.start?.toDate ? s.start.toDate() : s.start,
          end: s.end?.toDate ? s.end.toDate() : s.end,
        };
      });
      setEvents(data);
    });
    return unsub;
  }, []);

  const addBirthdayEvent = useCallback(
    async (student) => {
      if (!(student?.dataNascita && user)) return;
      let birth = null;
      if (student.dataNascita instanceof Date) birth = moment(student.dataNascita);
      else if (student.dataNascita instanceof Timestamp) birth = moment(student.dataNascita.toDate());
      else if (typeof student.dataNascita === 'number') birth = moment(new Date(student.dataNascita));
      if (!birth) return;

      const now = moment();
      birth.year(now.year());
      if (birth.isBefore(now, 'day')) birth.add(1, 'year');

      try {
        const eventsCol = collection(db, 'events');
        const qy = query(
          eventsCol,
          where('userId', '==', user.uid),
          where('title', '==', `Compleanno - ${student.nome} ${student.cognome}`)
        );
        const snap = await getDocs(qy);
        if (snap.empty) {
          await addDoc(eventsCol, {
            userId: user.uid,
            title: `Compleanno - ${student.nome} ${student.cognome}`,
            start: birth.toDate(),
            end: moment(birth).add(1, 'hour').toDate(),
            recurrence: 'FREQ=YEARLY',
            category: 'Compleanno',
          });
        }
      } catch (e) {
        console.error('Errore addBirthdayEvent:', e);
      }
    },
    [user]
  );

  const updateBirthdayEvent = useCallback(
    async (student) => {
      if (!(student?.dataNascita && user)) return;
      let birth = null;
      if (student.dataNascita instanceof Date) birth = moment(student.dataNascita);
      else if (student.dataNascita instanceof Timestamp) birth = moment(student.dataNascita.toDate());
      else if (typeof student.dataNascita === 'number') birth = moment(new Date(student.dataNascita));
      if (!birth) return;

      const now = moment();
      birth.year(now.year());
      if (birth.isBefore(now, 'day')) birth.add(1, 'year');

      try {
        const eventsCol = collection(db, 'events');
        const qy = query(
          eventsCol,
          where('userId', '==', user.uid),
          where('title', '==', `Compleanno - ${student.nome} ${student.cognome}`)
        );
        const snap = await getDocs(qy);
        if (!snap.empty) {
          const ref = snap.docs[0].ref;
          await updateDoc(ref, {
            start: birth.toDate(),
            end: moment(birth).add(1, 'hour').toDate(),
          });
        } else {
          await addBirthdayEvent(student);
        }
      } catch (e) {
        console.error('Errore updateBirthdayEvent:', e);
      }
    },
    [user, addBirthdayEvent]
  );

  useEffect(() => {
    if (!events.length) {
      setUpcomingEventsCount(0);
      setBirthdayNotifications(0);
      return;
    }
    const now = moment().startOf('day');
    const endDate = moment().add(1, 'year').endOf('day');
    let up = 0;
    let bdays = 0;

    events.forEach((ev) => {
      let occurrences = [];
      if (ev.recurrence && ev.recurrence !== 'none') {
        try {
          const ruleOptions = {
            ...RRule.parseString(ev.recurrence),
            dtstart: ev.start,
            until: endDate.toDate(),
          };
          if (ev.category === 'Compleanno') {
            ruleOptions.freq = RRule.YEARLY;
            ruleOptions.bymonth = moment(ev.start).month() + 1;
            ruleOptions.bymonthday = moment(ev.start).date();
            ruleOptions.dtstart = now.toDate();
          }
          const rule = new RRule(ruleOptions);
          occurrences = rule.between(now.toDate(), endDate.toDate(), true);
        } catch (e) {
          console.error('Errore parsing RRULE:', e);
        }
      } else {
        if (moment(ev.start).isSameOrAfter(now) && moment(ev.start).isSameOrBefore(endDate)) {
          occurrences.push(ev.start);
        }
      }
      if (occurrences.length) {
        if (ev.category === 'Compleanno') bdays += occurrences.length;
        else up += occurrences.length;
      }
    });

    setUpcomingEventsCount(up);
    setBirthdayNotifications(bdays);
  }, [events]);

  // =======================
  // ALERT ASSENTI OLTRE 3 SETTIMANE
  // =======================
  useEffect(() => {
    if (!user) return;
    if (!students.length) return;
    if (!savedPrograms.length) return;

    const todayKey = moment().format('YYYYMMDD');
    const storageKey = `longAbsenceAlert-${user.uid}-${todayKey}`;
    if (localStorage.getItem(storageKey)) return;

    const flagged = computeLongAbsenceStudents(students, savedPrograms);
    if (flagged.length) {
      setLongAbsenceStudents(flagged);
      setAbsentAlertOpen(true);
      localStorage.setItem(storageKey, '1');
    }
  }, [user, students, savedPrograms]);

// =======================
  // PROGRAMMI SALVATI
  // =======================
  useEffect(() => {
    if (!user) return;
    const col = collection(db, 'programs');
    const qy = query(col, where('userId', '==', user.uid));
    const unsub = onSnapshot(qy, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => {
        const da = a.meetingDate ? new Date(a.meetingDate) : new Date(0);
        const dbb = b.meetingDate ? new Date(b.meetingDate) : new Date(0);
        return dbb - da;
      });
      setSavedPrograms(list);
    });
    return () => unsub();
  }, [user]);

  const handleLoadProgram = async (e) => {
    const pid = e.target.value;
    setSelectedProgramId(pid);
    if (!pid) return;
    try {
      const pDoc = await getDoc(doc(db, 'programs', pid));
      if (!pDoc.exists()) return notify('warning', 'Programma non trovato.');
      const data = pDoc.data();
      setDashboardTitle(data.dashboardTitle || 'Dashboard');
      setCatechists(data.catechists || '');
      setLessonPlan(data.lessonPlan || '');
      setMeetingDate(data.meetingDate || '');

      // replica presenze su students
      if (Array.isArray(data.presenze)) {
        for (const rec of data.presenze) {
          try {
            await updateDoc(doc(db, 'students', rec.studentId), { present: !!rec.present });
          } catch (e) {
            console.error('Errore update presence student:', rec.studentId, e);
          }
        }
      }
      notify('success', 'Programma caricato con successo!');
    } catch (e) {
      console.error('Errore handleLoadProgram:', e);
      notify('error', 'Errore durante il caricamento del programma.');
    }
  };

  const handleSaveProgram = () => setOpenSaveDialog(true);
  const cancelSaveProgram = () => setOpenSaveDialog(false);

  const confirmSaveProgram = async () => {
    setOpenSaveDialog(false);
    if (!meetingDate) return notify('warning', "Inserisci la data dell'incontro prima di salvare.");
    try {
      const col = collection(db, 'programs');
      const qy = query(col, where('userId', '==', user.uid), where('meetingDate', '==', meetingDate));
      const snap = await getDocs(qy);
      if (!snap.empty) {
        setConflictProgram(snap.docs[0]);
        setOpenConflictDialog(true);
        return;
      }
      await saveNewProgram();
    } catch (e) {
      console.error('Errore confirmSaveProgram:', e);
      notify('error', 'Errore durante il salvataggio del programma.');
    }
  };

  const saveNewProgram = async () => {
    try {
      const presenze = students.map((s) => ({ studentId: s.id, present: !!s.present }));
      await addDoc(collection(db, 'programs'), {
        userId: user.uid,
        dashboardTitle,
        catechists,
        lessonPlan,
        meetingDate,
        createdAt: Timestamp.now(),
        presenze,
      });
      notify('success', 'Programma salvato con successo!');
    } catch (e) {
      console.error('Errore saveNewProgram:', e);
      notify('error', 'Errore durante il salvataggio del programma.');
    }
  };

  const handleDeleteProgram = () => {
    if (!selectedProgramId) return notify('info', 'Seleziona un programma da eliminare.');
    setOpenDeleteProgramDialog(true);
  };
  const cancelDeleteProgram = () => setOpenDeleteProgramDialog(false);

  const confirmDeleteProgram = async () => {
    setOpenDeleteProgramDialog(false);
    try {
      await deleteDoc(doc(db, 'programs', selectedProgramId));
      setSelectedProgramId('');
      notify('success', 'Programma eliminato con successo!');
    } catch (e) {
      console.error('Errore delete program:', e);
      notify('error', "Errore durante l'eliminazione del programma.");
    }
  };

  // =======================
  // RESET CAMPI + PRESENZE
  // =======================
  const openResetDialogHandler = () => setOpenResetDialog(true);
  const closeResetDialogHandler = () => setOpenResetDialog(false);

  const confirmResetDashboard = async () => {
    if (!user) return;
    try {
      // Salva uno snapshot per poter ripristinare l'ultimo reset
      const snapshot = {
        catechists,
        meetingDate,
        lessonPlan,
        presenze: students.map((s) => ({ studentId: s.id, present: !!s.present })),
      };
      setLastResetSnapshot(snapshot);
      setCanRestoreReset(true);

      // Reset campi locali
      setCatechists('');
      setMeetingDate('');
      setLessonPlan('');

      // Salva reset su Firestore
      await savePersistentData(user.uid, {
        catechists: '',
        meetingDate: '',
        lessonPlan: '',
      });

      // Reset presenze studenti
      const operations = students.map((s) =>
        updateDoc(doc(db, 'students', s.id), { present: false }).catch((err) => {
          console.error('Errore reset presenza studente:', s.id, err);
        })
      );
      await Promise.all(operations);

      notify('success', 'Campi e presenze azzerati con successo. Niente panico: puoi ancora ripristinare l\'ultimo reset finché non chiudi o aggiorni la pagina.');
    } catch (err) {
      console.error('Errore conferma reset dashboard:', err);
      notify('error', 'Errore durante il reset dei campi.');
    } finally {
      setOpenResetDialog(false);
    }
  };



  const handleRestoreAfterReset = async () => {
    if (!user) return;
    if (!lastResetSnapshot) {
      notify('info', 'Non ci sono dati da ripristinare.');
      return;
    }
    try {
      const { catechists: oldCatechists, meetingDate: oldMeetingDate, lessonPlan: oldLessonPlan, presenze } = lastResetSnapshot;

      // Ripristina campi locali
      setCatechists(oldCatechists || '');
      setMeetingDate(oldMeetingDate || '');
      setLessonPlan(oldLessonPlan || '');

      // Salva nuovamente i dati su Firestore
      await savePersistentData(user.uid, {
        catechists: oldCatechists || '',
        meetingDate: oldMeetingDate || '',
        lessonPlan: oldLessonPlan || '',
      });

      // Ripristina presenze studenti
      if (Array.isArray(presenze) && presenze.length) {
        const ops = presenze.map((rec) =>
          updateDoc(doc(db, 'students', rec.studentId), { present: !!rec.present }).catch((err) => {
            console.error('Errore ripristino presenza studente:', rec.studentId, err);
          })
        );
        await Promise.all(ops);
      }

      setCanRestoreReset(false);
      notify('success', 'Ultimo reset annullato. Dati e presenze ripristinati.');
    } catch (err) {
      console.error('Errore handleRestoreAfterReset:', err);
      notify('error', 'Errore durante il ripristino dei dati.');
    }
  };
  const handleOverwriteProgram = async () => {
    if (!conflictProgram) return;
    try {
      const presenze = students.map((s) => ({ studentId: s.id, present: !!s.present }));
      await updateDoc(doc(db, 'programs', conflictProgram.id), {
        dashboardTitle,
        catechists,
        lessonPlan,
        meetingDate,
        presenze,
        updatedAt: Timestamp.now(),
      });
      setConflictProgram(null);
      setOpenConflictDialog(false);
      notify('success', 'Programma sovrascritto con successo!');
    } catch (e) {
      console.error('Errore overwrite:', e);
      notify('error', 'Errore durante la sovrascrittura del programma.');
    }
  };

  const handleRenameProgram = () => {
    setOpenConflictDialog(false);
    setOpenRenameDialog(true);
  };
  const cancelRenameProgram = () => {
    setOpenRenameDialog(false);
    setNewMeetingDate('');
  };
  const confirmRenameProgram = async () => {
    if (!newMeetingDate) return notify('info', 'Seleziona una nuova data.');
    try {
      const col = collection(db, 'programs');
      const qy = query(col, where('userId', '==', user.uid), where('meetingDate', '==', newMeetingDate));
      const snap = await getDocs(qy);
      if (!snap.empty) {
        return notify('warning', 'Esiste già un programma per la nuova data.');
      }
      const presenze = students.map((s) => ({ studentId: s.id, present: !!s.present }));
      await updateDoc(doc(db, 'programs', conflictProgram.id), {
        dashboardTitle,
        catechists,
        lessonPlan,
        meetingDate: newMeetingDate,
        presenze,
        updatedAt: Timestamp.now(),
      });
      setConflictProgram(null);
      setOpenRenameDialog(false);
      setNewMeetingDate('');
      notify('success', 'Programma rinominato con successo!');
    } catch (e) {
      console.error('Errore rename:', e);
      notify('error', 'Errore durante la rinomina del programma.');
    }
  };

  // =======================
  // EDITABLE TITLE + CAMPI
  // =======================
  const handleTitleClick = () => setIsEditingTitle(true);
  const handleTitleChange = (e) => setDashboardTitle(e.target.value);
  const handleTitleBlur = () => {
    setIsEditingTitle(false);
    if (user) savePersistentData(user.uid, { dashboardTitle });
  };

  const handleCatechistsChange = (e) => {
    setCatechists(e.target.value);
    if (user) savePersistentData(user.uid, { catechists: e.target.value });
  };
  const handleLessonPlanChange = (e) => {
    setLessonPlan(e.target.value);
    if (user) savePersistentData(user.uid, { lessonPlan: e.target.value });
  };
  const handleMeetingDateChange = (e) => {
    setMeetingDate(e.target.value);
    if (user) savePersistentData(user.uid, { meetingDate: e.target.value });
  };
  const handleYearLevelChange = (e) => {
    const v = e.target.value;
    setYearLevel(v);
    if (user) savePersistentData(user.uid, { yearLevel: v });
  };

  // =======================
  // GENERAZIONE REPORT
  // =======================

  // <NUOVO> Logger su Firestore per ogni file caricato, con storagePath + metadati file
  const logReportDoc = async ({
    name,
    storagePath,
    mimeType = 'application/pdf',
    bytes = null,
    kind,               // 'settimanale' | 'mensile' | 'statistiche' | 'statistiche-excel' | 'privacy'
    extra = {},         // metadati aggiuntivi
  }) => {
    try {
      const userEmail = (auth.currentUser && auth.currentUser.email) || 'sconosciuto';
      const extracted = extractMeetingDateFromName(name);
      await addDoc(collection(db, 'reports'), {
        name,
        fileName: name,
        storagePath,
        mimeType,
        bytes,
        kind,
        userId: user?.uid || null,
        uploadedBy: userEmail,
        dashboardTitle: dashboardTitle || '',
        catechists: catechists || '',
        yearLevel: yearLevel || '',
        yearFolder: yearFolder || '',
        createdAt: Timestamp.now(),
        trashed: false,
        meetingDate: extra.meetingDate ? extra.meetingDate : (extracted || null),
        ...extra,
      });
    } catch (e) {
      console.error('Errore logReportDoc:', e);
    }
  };

  // Validazioni comuni
  function requireBasicsOrThrow() {
    if (!(dashboardTitle && dashboardTitle.trim())) {
      throw new Error('Titolo Dashboard mancante: imposta un titolo.');
    }
    if (!yearFolder) {
      throw new Error("Seleziona l'Anno (cartella) accanto al titolo.");
    }
    if (!(catechists && catechists.trim())) {
      throw new Error('Nomi catechisti mancanti: compila il campo Catechisti.');
    }
  }
  // WEEKLY
  const handleGeneratePdf = () => setOpenGeneratePdfDialog(true);
  const cancelGeneratePdf = () => setOpenGeneratePdfDialog(false);


  async function doWeeklyUpload() {
    try {
      const currentDate = meetingDate ? moment(meetingDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
      const yearLabel = getYearLabel();

      const names = buildReportNames({
        type: 'settimanale',
        dashboardTitle,
        catechists,
        dateLabel: currentDate,
        yearLabel,
      });

      const longAbsence = computeLongAbsenceStudents(students, savedPrograms);
      const longAbsenceNames = longAbsence
        .map((s) => `${s.cognome || ''} ${s.nome || ''}`.trim())
        .filter(Boolean);

      setUploadDlgMsg('Preparo il PDF…');
      showUploadDialog(`Caricamento: ${names.pdfName}`);

      const { pdfBlob } = await generatePdfReport(
        students,
        currentDate,
        lessonPlan,
        dashboardTitle,
        catechists,
        yearLabel,
        longAbsenceNames
      );

      try { openBlobForCatechist(pdfBlob, names.pdfName, { alsoDownload: true }); } catch (e) { console.error(e); }

      setUploadDlgMsg('Carico su Storage…');
      setUploadPct(35);
      const path = `${yearFolder}/${names.pdfName}`;

      await uploadFileWithMetadata(pdfBlob, {
        storagePath: path,
        metadata: { contentType: 'application/pdf' },
      });
      setUploadPct(100);

      await logReportDoc({
        name: names.pdfName,
        storagePath: path,
        mimeType: pdfBlob.type || 'application/pdf',
        bytes: typeof pdfBlob.size === 'number' ? pdfBlob.size : null,
        kind: 'settimanale',
        extra: {
          meetingDate: meetingDate ? new Date(meetingDate) : null,
          yearLabel,
        },
      });

      notify('success', 'Report settimanale generato e caricato su Storage!');
    } catch (e) {
      console.error('Errore doWeeklyUpload:', e);
      notify('error', 'Errore durante generazione o upload del report settimanale.');
    } finally {
      closeUploadDialog();
    }
  }

  // MONTHLY
  const handleGenerateMonthlyPdf = () => setOpenMonthlyReportDialog(true);
  const cancelGenerateMonthlyReport = () => setOpenMonthlyReportDialog(false);
  const confirmGenerateMonthlyPdf = async () => {
    setOpenMonthlyReportDialog(false);
    try {
      requireBasicsOrThrow();
      const m = selectedReportMonth - 1; // moment 0-index
      const y = selectedReportYear;
      const monthlyPrograms = savedPrograms.filter((p) => {
        const d = moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        return d.isValid() && d.month() === m && d.year() === y;
      });
      if (!monthlyPrograms.length) {
        return notify('info', 'Non ci sono programmi per il mese/anno selezionati.');
      }
      const reportMonthYear = moment().month(m).year(y).format('MMMM YYYY');
      const additionalTitle = `Report del mese di ${reportMonthYear}`;

      const yearLabel = getYearLabel();
      const names = buildReportNames({
        type: 'mensile',
        dashboardTitle,
        catechists,
        dateLabel: additionalTitle,
        yearLabel,
      });

      askConfirm({
        type: 'monthly',
        title: 'Conferma Report Mensile',
        files: [{ name: names.pdfName }],
      });
    } catch (e) {
      notify('warning', e.message);
    }
  };

  async function doMonthlyUpload() {
    try {
      const m = selectedReportMonth - 1;
      const y = selectedReportYear;

      const monthlyPrograms = savedPrograms.filter((p) => {
        const d = moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        return d.isValid() && d.month() === m && d.year() === y;
      });

      const attendanceData = students.map((s) => {
        const attendanceRecords = monthlyPrograms.map((program) => {
          const rec = (program.presenze || []).find((r) => r.studentId === s.id);
          return rec ? !!rec.present : false;
        });
        const totalPresences = attendanceRecords.filter(Boolean).length;
        return {
          cognome: s.cognome,
          nome: s.nome,
          presenze: attendanceRecords,
          totalPresences,
          photoURL: s.photoURL,
          privacyConsent: s.privacyConsent,
        };
      });

      const reportMonthYear = moment().month(m).year(y).format('MMMM YYYY');
      const additionalTitle = `Report del mese di ${reportMonthYear}`;

      const yearLabel = getYearLabel();
      const names = buildReportNames({
        type: 'mensile',
        dashboardTitle,
        catechists,
        dateLabel: additionalTitle,
        yearLabel,
      });

      setUploadDlgMsg('Preparo il PDF mensile…');
      showUploadDialog(`Caricamento: ${names.pdfName}`);

      const longAbsenceForMonth = computeLongAbsenceStudents(students, monthlyPrograms);
      const longAbsenceNames = longAbsenceForMonth
        .map((s) => `${s.cognome || ''} ${s.nome || ''}`.trim())
        .filter(Boolean);

      const { pdfBlob } = await generateMonthlyPdfReport(
        dashboardTitle,
        catechists,
        additionalTitle,
        monthlyPrograms,
        attendanceData,
        yearLabel,
        longAbsenceNames
      );

      openBlobForCatechist(pdfBlob, names.pdfName, { alsoDownload: true });

      setUploadDlgMsg('Carico su Storage…');
      setUploadPct(35);
      const path = `${yearFolder}/${names.pdfName}`;

      await uploadFileWithMetadata(pdfBlob, {
        storagePath: path,
        metadata: { contentType: 'application/pdf' },
      });
      setUploadPct(100);

      await logReportDoc({
        name: names.pdfName,
        storagePath: path,
        mimeType: pdfBlob.type || 'application/pdf',
        bytes: typeof pdfBlob.size === 'number' ? pdfBlob.size : null,
        kind: 'mensile',
        extra: {
          reportMonth: m + 1,
          reportYear: y,
          yearLabel,
        },
      });

      notify('success', 'Report mensile generato e caricato su Storage!');
    } catch (e) {
      console.error('Errore doMonthlyUpload:', e);
      notify('error', 'Errore durante generazione o upload del report mensile.');
    } finally {
      closeUploadDialog();
    }
  }

  // STATS

  async function doStatsUpload() {
    try {
      const end = asOfDate ? moment(asOfDate, 'YYYY-MM-DD').endOf('day') : moment().endOf('day');
      const asOfLabel = end.format('DD/MM/YYYY');
      const consideredPrograms = savedPrograms.filter((p) => {
        if (!p.meetingDate) return false;
        const d = moment(p.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
        return d.isValid() && d.isSameOrBefore(end, 'day');
      });
      const totalMeetings = consideredPrograms.length;

      const presenceMap = new Map();
      consideredPrograms.forEach((p) => {
        (p.presenze || []).forEach((rec) => {
          if (rec.present) presenceMap.set(rec.studentId, (presenceMap.get(rec.studentId) || 0) + 1);
        });
      });

      const orderedStudents = [...students].sort((a, b) => (a.cognome || '').localeCompare(b.cognome || ''));

      const yearLabel = getYearLabel();
      const names = buildReportNames({
        type: 'statistiche presenze',
        dashboardTitle,
        catechists,
        dateLabel: `fino al ${asOfLabel}`,
        yearLabel,
      });

      setUploadDlgMsg('Preparo il PDF statistiche…');
      showUploadDialog(`Caricamento: ${names.pdfName}`);

      const { pdfBlob } = await generateStatsPdfReport(
        {
          dashboardTitle,
          catechists,
          asOfDateLabel: `fino al ${asOfLabel}`,
          students: orderedStudents,
          totalMeetings,
          presenceMap,
        },
        null,
        yearLabel
      );

      openBlobForCatechist(pdfBlob, names.pdfName, { alsoDownload: true });

      setUploadDlgMsg('Carico PDF su Storage…');
      setUploadPct(35);
      const pdfPath = `${yearFolder}/${names.pdfName}`;
      await uploadFileWithMetadata(pdfBlob, {
        storagePath: pdfPath,
        metadata: { contentType: 'application/pdf' },
      });

      await logReportDoc({
        name: names.pdfName,
        storagePath: pdfPath,
        mimeType: pdfBlob.type || 'application/pdf',
        bytes: typeof pdfBlob.size === 'number' ? pdfBlob.size : null,
        kind: 'statistiche',
        extra: { asOfDate: end.toDate(), yearLabel },
      });

      const excelRows = orderedStudents.map((s, i) => {
        const pres = presenceMap.get(s.id) || 0;
        const perc = totalMeetings > 0 ? Math.round((pres / totalMeetings) * 100) : 0;
        return {
          n: i + 1,
          cognome: s.cognome || '',
          nome: s.nome || '',
          photoURL: s.photoURL || '',
          presenze: pres,
          totIncontri: totalMeetings,
          percentuale: perc,
        };
      });

      const longAbsenceForStats = computeLongAbsenceStudents(orderedStudents, consideredPrograms);
      const longAbsenceNames = longAbsenceForStats
        .map((s) => `${s.cognome || ''} ${s.nome || ''}`.trim())
        .filter(Boolean);

      setUploadDlgMsg('Preparo il file Excel…');
      const { xlsxBlob, suggestedName } = await generateExcelStats({
        rows: excelRows,
        asOfDateLabel: asOfLabel,
        yearLabel,
        longAbsenceNames,
        returnBlob: true,
      });
      if (!xlsxBlob) {
        throw new Error('Excel non generato: xlsxBlob mancante');
      }

      const xlsxName = buildReportNames({
        type: 'statistiche presenze',
        dashboardTitle,
        catechists,
        dateLabel: `fino al ${asOfLabel}`,
        yearLabel,
      }).xlsxName || suggestedName;

      setUploadDlgMsg('Carico Excel su Storage…');
      const xlsxPath = `${yearFolder}/${xlsxName}`;

      await uploadFileWithMetadata(xlsxBlob, {
        storagePath: xlsxPath,
        metadata: { contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
      });
      setUploadPct(100);

      await logReportDoc({
        name: xlsxName,
        storagePath: xlsxPath,
        mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        bytes: typeof xlsxBlob.size === 'number' ? xlsxBlob.size : null,
        kind: 'statistiche',
        extra: { asOfDate: end.toDate(), yearLabel },
      });

      notify('success', 'Statistiche (PDF + Excel) generate e caricate su Storage!');
    } catch (e) {
      console.error('Errore doStatsUpload:', e);
      notify('error', 'Errore durante generazione o upload delle statistiche.');
    } finally {
      closeUploadDialog();
    }
  }

  // Conferma unificata → dispatch
  const confirmProceed = async () => {
    const t = confirmPayload.type;
    closeConfirm();
    if (t === 'weekly') return doWeeklyUpload();
    if (t === 'monthly') return doMonthlyUpload();
    if (t === 'stats') return doStatsUpload();
  };

  // Builder payload
  const buildConfirmForWeekly = () => {
    const dateLabel = meetingDate ? moment(meetingDate, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';
    const yearLabel = getYearLabel();
    const names = buildReportNames({ type: 'settimanale', dashboardTitle, catechists, dateLabel, yearLabel });
    return {
      type: 'weekly',
      title: 'Conferma invio Report Settimanale',
      files: [{ name: names.pdfName }],
    };
  };

  const buildConfirmForStats = () => {
    const yearLabel = getYearLabel();
    const end = asOfDate ? moment(asOfDate, 'YYYY-MM-DD') : moment();
    const names = buildReportNames({
      type: 'statistiche presenze',
      dashboardTitle,
      catechists,
      dateLabel: `fino al ${end.format('DD/MM/YYYY')}`,
      yearLabel,
    });
    return {
      type: 'stats',
      title: 'Conferma invio Statistiche',
      files: [{ name: names.pdfName }, ...(names.xlsxName ? [{ name: names.xlsxName }] : [])],
    };
  };

  // =======================
  // PRIVACY: helpers
  // =======================

  function fetchPrivacyPolicy() {
    return (async () => {
      try {
        const ref = doc(db, 'settings', 'privacyPolicy');
        const s = await getDoc(ref);
        if (s.exists()) {
          const d = s.data();
          setPrivacyDoc({ text: d.text || '', version: d.version || '' });
        } else {
          setPrivacyDoc({ text: 'Policy non disponibile.', version: '' });
        }
      } catch (e) {
        console.error('Errore fetchPrivacyPolicy:', e);
        setPrivacyDoc({ text: 'Impossibile caricare la policy al momento.', version: '' });
      }
    })();
  }

  const refusePrivacy = async () => {
    try { await auth.signOut(); } catch (err) {}
    if (typeof navigate === 'function') navigate('/login');
  };

  async function sha256Hex(str) {
  try {
    if (typeof crypto !== 'undefined' && crypto.subtle && typeof crypto.subtle.digest === 'function') {
      const enc = new TextEncoder().encode(str);
      const buf = await crypto.subtle.digest('SHA-256', enc);
      return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2,'0')).join('');
    }
  } catch (e) {
    console.error('sha256Hex crypto.subtle error:', e);
  }
  // Fallback hash (non critico, solo per avere un identificativo stabile)
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(16);
}


async function generatePrivacyPdf({ text = '', version = '', user, signatureDataUrl = '', hash = '', ts = new Date() }) {
    const docPdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const margin = 48;
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const pageHeight = docPdf.internal.pageSize.getHeight();
    let y = margin;

    docPdf.setFont('Helvetica', 'bold');
    docPdf.setFontSize(16);
    docPdf.text('Informativa Privacy & Consenso', pageWidth/2, y, { align: 'center' });
    y += 28;

    docPdf.setFont('Helvetica', 'normal');
    docPdf.setFontSize(11);
    const lines = [
      `Versione: ${version || '-'}`,
      `Email: ${user?.email || ''}`,
      `UID: ${user?.uid || ''}`,
      `Data accettazione: ${ts.toLocaleString()}`,
      `Hash: ${hash}`,
    ];
    lines.forEach(l => { docPdf.text(l, margin, y); y += 16; });
    y += 8;
    docPdf.setDrawColor(200);
    docPdf.line(margin, y, pageWidth - margin, y);
    y += 20;

    // Render del testo (HTML o plain)
    const raw = String(text || '');
    const asPlain = raw
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/p\s*>/gi, '\n\n')
      .replace(/<[^>]+>/g, '');
    const paras = docPdf.splitTextToSize(asPlain, pageWidth - margin*2);
    docPdf.setFontSize(10.5);
    for (let i = 0; i < paras.length; i++) {
      if (y > pageHeight - margin - 120) { docPdf.addPage(); y = margin; }
      docPdf.text(paras[i], margin, y);
      y += 14;
    }


    const blob = docPdf.output('blob');
    const safeMail = (user?.email || 'utente').replace(/[^a-z0-9@._-]/gi,'_');
    const fileName = `Privacy_${version || 'v'}__${safeMail}.pdf`;
    return { pdfBlob: blob, fileName };
  }

  const acceptPrivacy = async () => {
    try {
      if (!user) { notify('warning','Utente non autenticato.'); return; }
      if (!acceptChecked) { notify('info','Conferma di aver letto e compreso la policy.'); return; }
      if (!yearFolder) { notify('warning',"Seleziona l'Anno del gruppo prima di dare il consenso."); return; }
      const ts = new Date();
      const hash = await sha256Hex(`${user.uid}#${ts.getTime()}`);

      const userRef = doc(db, 'users', user.uid);
      await setDoc(userRef, {
        privacyAccepted: true,
        privacyAcceptedAt: Timestamp.fromDate(ts),
        privacyVersion: privacyDoc.version || '',
        privacyHash: hash,
        yearLevel: yearLevel || '',
        yearFolder: yearFolder || '',
      }, { merge: true });

      // Salva ricevuta .txt su Storage e logga su reports (kind: 'privacy')
      try {
        const receiptText =
`Privacy Policy - Ricevuta di accettazione
Versione: ${privacyDoc.version || '-'}
Utente: ${user.displayName || ''} <${user.email || ''}>
UID: ${user.uid}
Accettata il: ${ts.toLocaleString()}
HASH: ${hash}

Estratto policy (inizio):
${(privacyDoc.text || '').slice(0, 1500)}...`;

        const receiptBlob = new Blob([receiptText], { type: 'text/plain' });
        const fileName = `Privacy_${privacyDoc.version || 'v'}__${(user.email || 'utente')}.txt`;
        const storagePath = `${yearFolder || 'Privacy'}/${fileName}`;

        await uploadFileWithMetadata(receiptBlob, {
          storagePath,
          metadata: { contentType: 'text/plain' },
        });

        await logReportDoc({
          name: fileName,
          storagePath,
          mimeType: 'text/plain',
          bytes: receiptBlob.size,
          kind: 'privacy',
          extra: { privacyVersion: privacyDoc.version || '', privacyHash: hash },
        });
      } catch (err) {
        console.error('Errore salvataggio ricevuta privacy:', err);
        notify('warning','Consenso salvato, ma errore nel salvataggio della ricevuta su Storage.');
      }

      setPrivacyAccepted(true);
      setPrivacyModalOpen(false);
      setAcceptChecked(false);
      notify('success','Consenso registrato con successo.');
    } catch (e) {
      console.error('Errore acceptPrivacy:', e);
      notify('error','Errore nel salvataggio del consenso.');
    }
  };

  // Download privacy PDF in view mode including last stored hash & signature
  const handleDownloadPrivacyPdf = async () => {
    try {
      setPrivacyDownloadBusy(true);
      const { pdfBlob } = await generatePrivacyPdf({
        text: (privacyDoc?.text || ''),
        version: (privacyDoc?.version || ''),
        user,
        signatureDataUrl: privacyStoredSignature || undefined,
        hash: privacyStoredHash || '',
        ts: privacyStoredAcceptedAt || new Date(),
      });
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      const safeMail = (user?.email||'utente').replace(/[^a-z0-9@._-]/gi,'_');
      a.download = `Privacy_${privacyDoc?.version||'v'}__${safeMail}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      console.error('Errore download privacy PDF:', e);
    } finally {
      setPrivacyDownloadBusy(false);
    }
  };

  // Load last stored privacy hash & signature when entering view mode
  useEffect(() => {
    (async () => {
      try {
        if (!user) return;
        if (!privacyViewMode) return;
        const uref = doc(db, 'users', user.uid);
        const usnap = await getDoc(uref);
        if (usnap.exists()) {
          const udata = usnap.data() || {};
          setPrivacyStoredHash(udata.privacyHash || '');
          setPrivacyStoredSignature(udata.privacySignature || '');
        }
      } catch (e) {
        console.error('Errore caricamento dati privacy (view mode):', e);
      }
    })();
  }, [privacyViewMode, user]);

  // =======================
  // RENDER
  // =======================

  return (
    <div className="dashboard-container">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <div className="dashboard-content">
        {/* AppBar */}
        <AppBar position="fixed" color="primary" className="app-bar">
          <Toolbar sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'center', gap: 1, minHeight: 64 }}>
            <IconButton edge="start" color="inherit" aria-label="menu" onClick={toggleSidebar} className="menu-button">
              <FaBars />
            </IconButton>

            <Typography variant="h6" className="app-title">
              {isEditingTitle ? (
                <TextField
                  value={dashboardTitle}
                  onChange={handleTitleChange}
                  onBlur={handleTitleBlur}
                  autoFocus
                  size="small"
                  InputProps={{
                    style: {
                      color: 'white',
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      borderRadius: 4,
                    },
                  }}
                />
              ) : (
                <span onClick={handleTitleClick} className="dashboard-title">
                  {dashboardTitle}
                </span>
              )}
            </Typography>

            {/* Selettore Anno */}
            <FormControl variant="outlined" size="small" sx={{ minWidth: 160, ml: 2 }}>
              <InputLabel id="year-level-label" sx={{ color: '#fff' }}>Anno</InputLabel>
              <Select
                labelId="year-level-label"
                value={yearLevel}
                onChange={handleYearLevelChange}
                label="Anno"
                sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#fff' } }}
              >
                <MenuItem value=""><em>Seleziona…</em></MenuItem>
                {YEAR_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {currentOccasion && !appbarHighlightText && (
              <Box
                sx={{
                  ml: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'rgba(255,255,255,0.16)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  maxWidth: { xs: 200, sm: 280 },
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{ fontSize: 18, lineHeight: 1 }}>
                  {currentOccasion.icon || '✨'}
                </span>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {currentOccasion.title}
                </Typography>
              </Box>
            )}

            {appbarHighlightText && (
              <Tooltip title={appbarHighlightText} arrow enterDelay={300}><Box
                sx={{
                  ml: 2,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 999,
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  maxWidth: 260,
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                <span style={{ marginRight: 6 }}>{appbarHighlightDateLabel} · </span>
                {appbarHighlightIcon && (
                  <span style={{ marginRight: 8 }}>
                    {appbarHighlightIcon}
                  </span>
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: 'white',
                    fontWeight: 500,
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                  }}
                  noWrap
                >
                  {appbarHighlightText}
                </Typography>
              </Box></Tooltip>
            )}

            <div style={{ flexGrow: 1 }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
              <Typography variant="subtitle1" className="current-date">
                {currentDateFormatted}
              </Typography>

              <FormControl variant="outlined" size="small" style={{ minWidth: 200 }}>
                <InputLabel id="select-program-label" sx={{ color: '#fff' }}>Programmi Salvati</InputLabel>
                <Select
                  labelId="select-program-label"
                  id="select-program"
                  value={selectedProgramId}
                  onChange={handleLoadProgram}
                  label="Programmi Salvati"
                >
                  <MenuItem value="">
                    <em>Seleziona un Programma</em>
                  </MenuItem>
                  {savedPrograms.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {`Programma del ${p.meetingDate ? moment(p.meetingDate).format('DD/MM/YYYY') : '-'}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button variant="contained" color="secondary" onClick={handleSaveProgram}>
                Salva Programma
              </Button>

              <Button variant="contained" color="secondary" sx={{ textTransform: 'uppercase', color: '#fff' }} onClick={handleDeleteProgram}>
                Elimina Programma
              </Button>

              <Tooltip title="Reset campi e presenze">
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={openResetDialogHandler}
                  sx={{ width: 40, height: 40 }}
                >
                  <FaRedoAlt />
                </IconButton>
              </Tooltip>

              <Tooltip title={canRestoreReset ? 'Ripristina ultimo reset' : 'Nessun reset recente da ripristinare'}>
                <span>
                  <IconButton
                    edge="end"
                    color="inherit"
                    onClick={handleRestoreAfterReset}
                    sx={{ width: 40, height: 40, ml: 0.5 }}
                    disabled={!canRestoreReset}
                  >
                    <FaUndoAlt />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Visualizza Calendario">
                <IconButton edge="end" color="inherit" onClick={() => { navigate('/CalendarPage'); toggleSidebar(); }}>
                  <Badge
                    badgeContent={upcomingEventsCount}
                    color="error"
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                  >
                    <Badge
                      badgeContent={birthdayNotifications}
                      color="warning"
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      sx={{ '& .MuiBadge-badge': { fontSize: '0.7rem' } }}
                    >
                      <FaCalendarAlt />
                    </Badge>
                  </Badge>
                </IconButton>
              </Tooltip>

              <Tooltip title="Libreria">
                <IconButton edge="end" color="inherit" onClick={() => { navigate('/Library'); toggleSidebar(); }}>
                  <FaBook />
                </IconButton>
              </Tooltip>

              {/* Pulsante Privacy stile icona Calendar */}
              <Tooltip title="Privacy">
                <IconButton
                  edge="end"
                  color="inherit"
                  onClick={openPrivacyView}
                  sx={{ width: 40, height: 40 }}
                >
                  <FaUserShield />
                </IconButton>
              </Tooltip>
            </div>
    
          {appbarHighlightText && (
            <Box
              sx={{
                display: { xs: 'flex', md: 'none' },
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 1,
                px: 2,
                pr: { xs: 0, sm: 0 },
                textAlign: 'center',
              }}
            >
              <Tooltip title={appbarHighlightText}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.75,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 999,
                    bgcolor: 'rgba(255,255,255,0.12)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    maxWidth: '100%',
                    mx: 'auto',
                  }}
                >
                  {appbarHighlightIcon && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.75 }}>
                      {appbarHighlightIcon}
                    </Box>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      letterSpacing: 0.25,
                      textAlign: 'center',
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {appbarHighlightDateLabel} · {appbarHighlightText}
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          )}
      </Toolbar>
        </AppBar>
         <Box sx={{ height: 16 }} />

        {/* Contenuto */}
        <Container maxWidth="lg" className="dashboard-container-content">
          <Grid container spacing={2}>
            {/* Catechisti */}
            <Grid item xs={12} md={6}>
              <Paper className="paper" sx={{ pt: 2 }}>
                <Typography variant="h6" className="section-title">
                  Nomi dei Catechisti
                </Typography>
                <p />
                <TextField
                  label="Inserisci i nomi dei catechisti"
                  value={catechists}
                  onChange={handleCatechistsChange}
                  fullWidth
                  variant="outlined"
                  className="input-box"
                  size="small"
                />
              </Paper>
            </Grid>

            {/* Data incontro */}
            <Grid item xs={12} md={6}>
              <Paper className="paper" sx={{ pt: 2 }}>
                <Typography variant="h6" className="section-title">
                  Data dell'Incontro di Catechismo
                </Typography>
                <p />
                <TextField
                  label="Inserisci la data dell'incontro"
                  type="date"
                  variant="outlined"
                  value={meetingDate}
                  onChange={handleMeetingDateChange}
                  fullWidth
                  className="input-box"
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Paper>
            </Grid>

            {/* Programma */}
            <Grid item xs={12}>
              <Paper className="paper">
                <Typography variant="h6" className="section-title">
                  Programma dell'incontro del Giorno
                </Typography>
                <p />
                <TextField
                  label="Scrivi qui il programma della lezione"
                  multiline
                  rows={4}
                  variant="outlined"
                  value={lessonPlan}
                  onChange={handleLessonPlanChange}
                  fullWidth
                  className="input-box"
                  size="small"
                />
              </Paper>
            </Grid>

            {/* Azioni */}
            <Grid item xs={12} className="actions-container">
              <Button
                variant="contained"
                color="primary"
                onClick={() => setAddOpen(true)}
                className="add-button"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                AGGIUNGI STUDENTE
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleGeneratePdf}
                className="generate-pdf-button"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                INVIO SETTIMANALE
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={handleGenerateMonthlyPdf}
                className="generate-monthly-pdf-button"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                INVIO MENSILE
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenStatsDialog(true)}
                className="compact-btn"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                INVIO STATISTICHE
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenStatsOverview(true)}
                className="compact-btn"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                CRUSCOTTO PRESENZE
              </Button>

              <Button
                variant="contained"
                color="primary"
                onClick={() => setToolsModalOpen(true)}
                className="compact-btn"
                sx={{ ml: { xs: 0, sm: 1 }, minWidth: { xs: 'auto', sm: 220 }, height: 48, fontWeight: 700, letterSpacing: 0.8 }}
              >
                STRUMENTI RAPIDI
              </Button>

            </Grid>

            {/* Tabella studenti */}
            <Grid item xs={12}>
              <Paper className="paper table-paper">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 2,
                    pt: 0.75,
                    pb: 1.5,
                    gap: 1,
                    flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  }}
                >
                  <Box
                    onClick={() => setSantoModalOpen(true)}
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 1.25,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: 'rgba(15, 23, 42, 0.02)',
                      border: '1px solid rgba(148, 163, 184, 0.45)',
                      color: 'inherit',
                      boxShadow: 'none',
                      fontSize: '0.78rem',
                      maxWidth: 320,
                      textAlign: 'center',
                      mr: 2,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        opacity: 0.9,
                      }}
                    >
                      <span role="img" aria-label="croce">
                        ✝️
                      </span>
                      Il Santo del giorno
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 500, opacity: 0.95 }}
                    >
                      {santoLoading
                        ? 'Caricamento in corso…'
                        : ((santoOfDay && santoOfDay.name) || santoDelGiorno || santoError || 'Dati non disponibili')}
                    </Typography>
                  </Box>
                  <Box
                    onClick={handleOpenLiturgyModal}
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 1.25,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: 'rgba(15, 23, 42, 0.02)',
                      border: '1px solid rgba(148, 163, 184, 0.45)',
                      color: 'inherit',
                      boxShadow: 'none',
                      fontSize: '0.78rem',
                      maxWidth: 320,
                      textAlign: 'center',
                      mr: 2,
                      cursor: 'pointer',
                    }}
                  >
                    <Typography
                      variant="overline"
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        opacity: 0.9,
                        whiteSpace: 'nowrap',
                        fontWeight: 700,
                      }}
                    >
                      <span role="img" aria-label="liturgia del giorno">
                        📖
                      </span>
                      LITURGIA DEL GIORNO
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 600, opacity: 0.95 }}
                    >
                      {liturgyDateIso
                        ? moment(liturgyDateIso).format('DD/MM/YYYY')
                        : moment().format('DD/MM/YYYY')}
                    </Typography>

                  </Box>

                  <Box
                    sx={{
                      display: 'inline-flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 0.25,
                      px: 1.25,
                      py: 0.45,
                      borderRadius: 999,
                      bgcolor: 'rgba(15, 23, 42, 0.02)',
                      border: '1px solid rgba(148, 163, 184, 0.45)',
                      color: 'inherit',
                      boxShadow: 'none',
                      fontSize: '0.78rem',
                      maxWidth: 320,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        mr: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        textTransform: 'uppercase',
                        letterSpacing: 0.4,
                        opacity: 0.9,
                      }}
                    >
                      <span role="img" aria-label="riepilogo presenze">
                        📊
                      </span>
                      Riepilogo {presenceSummaryDateLabel}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ fontWeight: 500, opacity: 0.95 }}
                    >
                      {totalStudents
                        ? `${presentStudents}/${totalStudents} presenti (${presencePercentage}%)`
                        : 'Nessuno studente registrato'}
                    </Typography>
                  </Box>
                </Box>
                <Table>
                  <TableHead className="table-header">
                    <TableRow>
                      <TableCell className="header-cell">Foto</TableCell>
                      <TableCell className="header-cell">Cognome</TableCell>
                      <TableCell className="header-cell">Nome</TableCell>
                      <TableCell className="header-cell">Data di Nascita</TableCell>
                      <TableCell className="header-cell">Presente</TableCell>
                      <TableCell className="header-cell">Consenso privacy</TableCell>
                      <TableCell className="header-cell">Azioni</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.map((s) => (
                      <TableRow key={s.id}>
                        <TableCell>
                          {s.photoURL ? <Avatar src={s.photoURL} alt={s.nome} /> : <Avatar>{s.nome?.charAt(0) || '?'}</Avatar>}
                        </TableCell>
                        <TableCell>{s.cognome}</TableCell>
                        <TableCell>{s.nome}</TableCell>
                        <TableCell>
                          {s.dataNascita
                            ? moment(s.dataNascita instanceof Timestamp ? s.dataNascita.toDate() : s.dataNascita).format('DD/MM/YYYY')
                            : ''}
                        </TableCell>
                        <TableCell>
                          <Checkbox checked={!!s.present} onChange={() => toggleAttendance(s)} color="primary" />
                        </TableCell>
                        <TableCell>{s.privacyConsent ? 'Sì' : 'No'}</TableCell>
                        <TableCell className="actions-cell">
                          <Button variant="outlined" color="primary" onClick={() => { setSelectedStudent(s); setDetailsOpen(true); }} size="small" className="action-button">
                            Dettagli
                          </Button>
                          <Button variant="outlined" color="secondary" onClick={() => { setSelectedStudent(s); setEditOpen(true); }} size="small" className="action-button">
                            Modifica
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={async () => {
                              try { await deleteDoc(doc(db, 'students', s.id)); }
                              catch (e) { notify('error', "Errore durante l'eliminazione dello studente."); }
                            }}
                            size="small"
                            className="action-button"
                          >
                            Elimina
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          </Grid>

          {/* Promemoria Domenicale per invio report */}
          <Dialog open={weeklyReminderOpen} onClose={() => setWeeklyReminderOpen(false)} maxWidth="sm" fullWidth>
            <DialogTitle>Promemoria invio report</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Oggi è domenica: ricorda di inviare il report settimanale dalla Dashboard.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setWeeklyReminderOpen(false)}>Chiudi</Button>
              <Button
                variant="contained"
                onClick={() => {
                  const key = `weeklyReminderDismissed-${moment().format('YYYYMMDD')}`;
                  localStorage.setItem(key, '1');
                  setWeeklyReminderOpen(false);
                  try { askConfirm(buildConfirmForWeekly()); } catch(e){}
                }}
              >
                Invia ora
              </Button>
            </DialogActions>
          </Dialog>

          {/* ===== Modale Privacy Policy (obbligatoria o view) ===== */}
          <Dialog
            open={privacyModalOpen && (!privacyAccepted || privacyViewMode)}
            onClose={(e, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            }}
            disableEscapeKeyDown
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
              Informativa sulla Privacy & Consenso
            </DialogTitle>
            <DialogContent dividers sx={{ maxHeight: 420 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Versione: {privacyDoc?.version ? privacyDoc.version : '-'}
              </Typography>

              <Box
                sx={{
                  mt: 2,
                  '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1, fontWeight: 700 },
                  '& p': { mt: 0, mb: 1.2 },
                  '& ul, & ol': { pl: 3, mb: 1.2 },
                  '& li': { mb: 0.5 },
                  '& a': { color: 'primary.main', textDecoration: 'underline' },
                  '& strong, & b': { fontWeight: 700 },
                  '& em, & i': { fontStyle: 'italic' },
                  '& br': { display: 'block', content: '""', my: 0.3 },
                }}
              >
                {privacyDoc?.text?.trim() ? (
                  isProbablyHtml(privacyDoc.text) ? (
                    <Typography component="div" sx={{ whiteSpace: 'normal' }}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: enhanceLinks(sanitizeHtml(privacyDoc.text)),
                        }}
                      />
                    </Typography>
                  ) : (
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {privacyDoc.text}
                    </Typography>
                  )
                ) : (
                  <Typography color="text.secondary">Policy non disponibile.</Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 1.2, px: 2, pb: 2 }}>
              {privacyViewMode ? (
                <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                  <Button onClick={() => { setPrivacyViewMode(false); setPrivacyModalOpen(false); }}>
                    Chiudi
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleDownloadPrivacyPdf}
                    disabled={privacyDownloadBusy}
                  >
                    {privacyDownloadBusy ? 'Preparazione…' : 'Scarica PDF firmato'}
                  </Button>
                </Box>
              ) : (
                <>
                  <FormControl
                    fullWidth
                    size="small"
                    sx={{ alignSelf: 'stretch', mb: 1 }}
                  >
                    <InputLabel id="privacy-year-level-label">Anno</InputLabel>
                    <Select
                      labelId="privacy-year-level-label"
                      value={yearLevel}
                      onChange={handleYearLevelChange}
                      label="Anno"
                    >
                      <MenuItem value="">
                        <em>Seleziona…</em>
                      </MenuItem>
                      {YEAR_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={acceptChecked}
                        onChange={(e) => setAcceptChecked(e.target.checked)}
                      />
                    }
                    label="Ho letto e compreso la policy"
                    sx={{ alignSelf: 'flex-start', mb: 1 }}
                  />
                  <Button variant="outlined" color="error" onClick={refusePrivacy} sx={{ fontWeight: 700 }}>
                    NON DO IL CONSENSO
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={acceptPrivacy}
                    disabled={!acceptChecked || !yearFolder}
                    sx={{ fontWeight: 700 }}
                  >
                    DO IL CONSENSO E INVIO
                  </Button>
                </>
              )}
            </DialogActions>
          </Dialog>
        
        {/* Santo del Giorno Modal */}
        <Modal open={santoModalOpen} onClose={handleSantoModalClose}>
          <Box
          sx={{
            p: 3,
            bgcolor: 'white',
            maxWidth: 420,
            m: '10% auto',
            borderRadius: 2,
            maxHeight: '80vh',
            overflowY: 'auto',
          }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <span role="img" aria-label="croce">
                ✝️
              </span>
              Il Santo del giorno
            </Typography>
            <Box
              component="form"
              onSubmit={handleSantoSearchSubmit}
              sx={{
                mt: 2,
                mb: 1.5,
                display: 'flex',
                gap: 1,
              }}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Cerca un santo per nome…"
                value={santoSearchQuery}
                onChange={(e) => setSantoSearchQuery(e.target.value)}
                autoComplete="off"
              />
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={santoLoading || !santoSearchQuery.trim()}
              >
                Cerca
              </Button>
            </Box>
            {santoLoading ? (
              <Box sx={{ mt: 2, mb: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Caricamento in corso...
                </Typography>
              </Box>
            ) : santoError ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {santoError}
              </Alert>
            ) : (
              <>
                {(santoDelGiorno || santoTitle) && (
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    {santoDelGiorno || santoTitle}
                  </Typography>
                )}
                {santoTitle && santoDelGiorno && (
                  <Typography variant="subtitle2" sx={{ mb: 1.5, opacity: 0.8 }}>
                    {santoTitle}
                  </Typography>
                )}
                {santoImage && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={santoImage}
                      alt={santoDelGiorno || 'Santo del giorno'}
                      style={{ width: '66%', maxWidth: '66%', margin: '0 auto', borderRadius: 8, display: 'block' }}
                    />
                  </Box>
                )}
                {santoDescription ? (
                  <Box
                    sx={{
                      mt: 1.5,
                      '& p': { mb: 1.2, lineHeight: 1.6 },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: convertPlainTextToHtml(santoDescription),
                    }}
                  />
                ) : (
                  <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 1.6 }}>
                    Le informazioni sul santo di oggi non sono al momento disponibili.
                  </Typography>
                )}
                {santoEffectivePageUrl && (
                  <Box sx={{ mt: 1.5 }}>
                    <Link
                      href={santoEffectivePageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      sx={{ fontSize: '0.85rem', fontWeight: 500 }}
                    >
                      Continua a leggere…
                    </Link>
                  </Box>
                )}
              </>
            )}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button variant="outlined" size="small" onClick={handleSantoModalClose}>
                Chiudi
              </Button>
            </Box>
          </Box>
        </Modal>

        <Modal open={liturgyModalOpen} onClose={handleCloseLiturgyModal}>
          <Box
            sx={{
              p: 3,
              bgcolor: 'white',
              maxWidth: 520,
              m: '8% auto',
              borderRadius: 2,
              maxHeight: '80vh',
              overflowY: 'auto',
            }}
          >
            <Typography
              variant="h6"
              sx={{ mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <span role="img" aria-label="liturgia del giorno">
                📖
              </span>
              {liturgyTitle || 'Liturgia del giorno'}
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, color: 'text.secondary' }}
            >
              {liturgySubtitle ||
                (liturgyDateIso
                  ? `Per il giorno ${moment(liturgyDateIso).format('DD/MM/YYYY')}`
                  : '')}
            </Typography>

            {liturgyError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {liturgyError}
              </Alert>
            )}

            {liturgyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 1,
                  '& p': { mb: 1.2 },
                  '& h1, & h2, & h3, & h4': { mt: 2, mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: liturgyContent }}
              />
            )}

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseLiturgyModal}>Chiudi</Button>
            </Box>
          </Box>
        </Modal>


</Container>

        {/* Snackbar promemoria (non bloccante) */}
        <Snackbar
          open={weeklyReminderOpen}
          onClose={() => {
            const key = `weeklyReminderDismissed-${moment().format('YYYYMMDD')}`;
            localStorage.setItem(key, '1');
            setWeeklyReminderOpen(false);
            try { askConfirm(buildConfirmForWeekly()); } catch(e){}
          }}
          autoHideDuration={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={() => {
                const key = `weeklyReminderDismissed-${moment().format('YYYYMMDD')}`;
                localStorage.setItem(key, '1');
                setWeeklyReminderOpen(false);
                try { askConfirm(buildConfirmForWeekly()); } catch(e){}
              }}>
                Invia ora
              </Button>
            }
            sx={{ width: '100%' }}
          >
            Ricorda di inviare il <strong>Report Settimanale</strong> di oggi.
          </Alert>
        </Snackbar>

        {/* FOOTER */}
        <footer className="dashboard-footer">
          <div className="social-buttons">
            <a href="https://www.tuosito.com" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Sito Internet">
              <FaGlobe />
            </a>
            <a href="https://www.facebook.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Facebook">
              <FaFacebookF />
            </a>
            <a href="https://wa.me/tuonumber" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="WhatsApp">
              <FaWhatsapp />
            </a>
            <a href="https://www.instagram.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Instagram">
              <FaInstagram />
            </a>
            <a href="https://www.pinterest.com/tuoprofilo" target="_blank" rel="noopener noreferrer" className="social-button" aria-label="Pinterest">
              <FaPinterestP />
            </a>
          </div>
          <p className="footer-text">&copy; {new Date().getFullYear()} Parrocchia del S.S. Redentore - Monserrato</p>
        </footer>

        {/* =======================
            DIALOGS UNIFICATI
        ======================== */}

        {/* Genera/Conferma settimanale */}
        <Dialog open={openGeneratePdfDialog} onClose={cancelGeneratePdf}>
          <DialogTitle sx={{ textAlign: 'center' }}>Genera Report Settimanale</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Verrà generato un PDF del report <b>settimanale</b> e caricato su <b>Archivio&nbsp;Reports</b>.
            </Typography>
            <Typography variant="body2" gutterBottom>
              Assicurati che <b>l'Anno</b> sia selezionato dal menu', e che <b>titolo Dashboard</b>, <b>catechisti</b>, <b>data incontro</b> e <b>programma</b> siano compilati correttamente.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelGeneratePdf}>Chiudi</Button>
            <Button
              variant="contained"
              onClick={() => askConfirm(buildConfirmForWeekly())}
            >
              Continua
            </Button>
          </DialogActions>
        </Dialog>

        {/* Selezione mese/anno + conferma mensile */}
        <Dialog open={openMonthlyReportDialog} onClose={cancelGenerateMonthlyReport}>
          <DialogTitle sx={{ textAlign: 'center' }}>Report Mensile</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <FormControl fullWidth variant="outlined" size="small">
                  <InputLabel id="select-month-label">Mese</InputLabel>
                  <Select
                    labelId="select-month-label"
                    value={selectedReportMonth}
                    label="Mese"
                    onChange={(e) => setSelectedReportMonth(e.target.value)}
                  >
                    {moment.months().map((m, i) => (
                      <MenuItem key={m} value={i + 1}>{m}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Anno"
                  type="number"
                  variant="outlined"
                  value={selectedReportYear}
                  onChange={(e) => setSelectedReportYear(Number(e.target.value))}
                  fullWidth
                  size="small"
                  inputProps={{ min: 2000, max: 2100 }}
                />
              </Grid>
            </Grid>
            <Typography variant="body2" sx={{ mt: 2 }}>
              Verrà generato un PDF del report <b>mensile</b> e caricato su <b>Archivio&nbsp;Reports</b>.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelGenerateMonthlyReport}>Chiudi</Button>
            <Button
              variant="contained"
              onClick={confirmGenerateMonthlyPdf}
            >
              Continua
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog param per statistiche */}
        <Dialog open={openStatsDialog} onClose={() => setOpenStatsDialog(false)}>
          <DialogTitle sx={{ textAlign: 'center' }}>Report Statistiche</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom>
              Genera le statistiche di presenza cumulative <b>fino alla data</b> selezionata (PDF + Excel).
            </Typography>
            <TextField
              label="Fino al"
              type="date"
              variant="outlined"
              value={asOfDate}
              onChange={(e) => setAsOfDate(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={{ mt: 1.5 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatsDialog(false)}>Chiudi</Button>
            <Button
              variant="contained"
              onClick={() => askConfirm(buildConfirmForStats())}
            >
              Continua
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cruscotto presenze (overview in modale) */}
        <Dialog
          open={openStatsOverview}
          onClose={() => setOpenStatsOverview(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
            Cruscotto presenze
          </DialogTitle>
          <DialogContent dividers>
            {overviewProgram ? (
              <>
                <Box sx={{ mb: 2 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="overview-program-label">Giorno da analizzare</InputLabel>
                    <Select
                      labelId="overview-program-label"
                      value={selectedOverviewProgramId || ''}
                      label="Giorno da analizzare"
                      onChange={(e) => setSelectedOverviewProgramId(e.target.value)}
                    >
                      {presenceTrend.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.label}
                          {overviewProgram && overviewProgram.id === p.id ? ' (selezionato)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Giorno selezionato:{' '}
                    <strong>
                      {overviewProgram.meetingDate
                        ? moment(overviewProgram.meetingDate).format('DD/MM/YYYY')
                        : 'N/D'}
                    </strong>
                  </Typography>
                </Box>

                <div className="presence-overview-grid">
                  <div className="presence-overview-card presence-overview-card--total">
                    <span className="presence-overview-label">Totale studenti nel programma</span>
                    <span className="presence-overview-value">{overviewTotalStudents}</span>
                  </div>
                  <div className="presence-overview-card presence-overview-card--present">
                    <span className="presence-overview-label">Presenti</span>
                    <span className="presence-overview-value">{overviewPresentStudents}</span>
                  </div>
                  <div className="presence-overview-card presence-overview-card--absent">
                    <span className="presence-overview-label">Assenti</span>
                    <span className="presence-overview-value">{overviewAbsentStudents}</span>
                  </div>
                </div>

                <div className="presence-overview-section">
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Percentuale presenze nel giorno selezionato
                  </Typography>
                  <div className="presence-overview-bar">
                    <div
                      className="presence-overview-bar-fill presence-overview-bar-fill--present"
                      style={{ width: `${overviewPresencePercentage}%` }}
                    />
                    <div
                      className="presence-overview-bar-fill presence-overview-bar-fill--absent"
                      style={{ width: `${overviewAbsencePercentage}%` }}
                    />
                  </div>
                  <div className="presence-overview-legend">
                    <span className="presence-overview-dot presence-overview-dot--present" />
                    <span className="presence-overview-legend-label">
                      Presenti ({overviewPresencePercentage}%)
                    </span>
                    <span className="presence-overview-dot presence-overview-dot--absent" />
                    <span className="presence-overview-legend-label">
                      Assenti ({overviewAbsencePercentage}%)
                    </span>
                  </div>
                </div>

                <div className="presence-overview-section">
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Andamento ultime presenze
                  </Typography>
                  {presenceTrendLast.length ? (
                    <>
                      <Box
                        sx={{
                          mt: 1.5,
                          mb: 1,
                          height: 140,
                          display: 'flex',
                          alignItems: 'flex-end',
                          gap: 1.2,
                        }}
                      >
                        {presenceTrendLast.map((p) => {
                          const isSelected = overviewProgram && p.id === overviewProgram.id;
                          const barHeight = Math.max(p.percentage || 0, 4);
                          return (
                            <Box
                              key={p.id}
                              sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                cursor: 'pointer',
                                height: '100%',
                              }}
                              onClick={() => setSelectedOverviewProgramId(p.id)}
                            >
                              <Box
                                sx={{
                                  width: '100%',
                                  borderRadius: 1,
                                  bgcolor: isSelected ? 'primary.main' : 'primary.light',
                                  opacity: isSelected ? 1 : 0.7,
                                  transition: 'all 0.2s ease',
                                  height: `${barHeight}%`,
                                }}
                              />
                              <Typography variant="caption" sx={{ mt: 0.5 }}>
                                {p.label}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: isSelected ? 700 : 400 }}
                              >
                                {p.percentage}%
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        Media ultime {presenceTrendLast.length} presenze:{' '}
                        <strong>{trendAverage}%</strong> — {trendDirectionLabel}.
                      </Typography>
                      {overviewProgram && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          Incontro selezionato:{' '}
                          <strong>{overviewPresencePercentage}%</strong>{' '}
                          {trendAverage
                            ? overviewPresencePercentage >= trendAverage
                              ? '(sopra la media)'
                              : '(sotto la media)'
                            : null}
                          .
                        </Typography>
                      )}
                      {bestDay && worstDay && (
                        <Typography
                          variant="caption"
                          sx={{ mt: 0.5, display: 'block', opacity: 0.8 }}
                        >
                          Migliore: {bestDay.label} ({bestDay.percentage}%){' '}
                          — Più critico: {worstDay.label} ({worstDay.percentage}%).
                        </Typography>
                      )}
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Salva almeno due programmi con presenze per visualizzare l&apos;andamento nel
                      tempo.
                    </Typography>
                  )}
                </div>
              </>
            ) : (
              <Typography variant="body2">
                Non ci sono ancora programmi salvati con presenze da mostrare nel cruscotto.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenStatsOverview(false)}>Chiudi</Button>
          </DialogActions>
        </Dialog>


        {/* Strumenti rapidi per il catechista */}
        <Dialog
          open={toolsModalOpen}
          onClose={() => setToolsModalOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', fontWeight: 700 }}>
            Strumenti rapidi per il catechista
          </DialogTitle>
          <DialogContent dividers>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Impostazioni comuni
              </Typography>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={includeOnlyPresentForTools}
                    onChange={(e) => setIncludeOnlyPresentForTools(e.target.checked)}
                  />
                )}
                label="Considera solo i presenti"
              />
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Studenti considerati dagli strumenti: {toolStudents.length}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Estrai uno studente a caso
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Utile per scegliere chi legge, chi risponde per primo o chi inizia un gioco.
              </Typography>
              <Button variant="outlined" onClick={handlePickRandomStudent}>
                Estrai studente
              </Button>
              {randomStudentResult && (
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid rgba(30,58,138,0.25)',
                    backgroundColor: 'rgba(30,58,138,0.04)',
                  }}
                >
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    Studente estratto:
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {randomStudentResult.cognome} {randomStudentResult.nome}
                  </Typography>
                </Box>
              )}
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Genera gruppi casuali
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Gli studenti vengono distribuiti in modo uniforme nei gruppi.
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                  label="Numero di gruppi"
                  type="number"
                  size="small"
                  value={groupsCount}
                  onChange={(e) => setGroupsCount(e.target.value)}
                  inputProps={{ min: 2 }}
                  sx={{ width: 180 }}
                />
                <Button variant="outlined" onClick={handleGenerateGroups}>
                  Genera gruppi
                </Button>
              </Box>
              {generatedGroups && generatedGroups.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {generatedGroups.map((group, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: '1px solid rgba(148,163,184,0.4)',
                        backgroundColor: 'rgba(148,163,184,0.08)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Gruppo {index + 1}
                      </Typography>
                      {group.length ? (
                        <Typography variant="body2">
                          {group.map((s) => `${s.cognome} ${s.nome}`).join(', ')}
                        </Typography>
                      ) : (
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                          Nessuno studente assegnato.
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Turni di lettura delle preghiere dei fedeli
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Seleziona fino a sei ragazzi per le preghiere dei fedeli e scrivi la preghiera affidata a ciascuno.
              </Typography>
              {readingTurnsLoading ? (
                <Box sx={{ py: 2 }}>
                  <LinearProgress />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  {readingTurns.map((row) => (
                    <Box
                      key={row.slot}
                      sx={{
                        p: 1,
                        borderRadius: 1.5,
                        border: '1px solid rgba(148,163,184,0.4)',
                        backgroundColor: 'rgba(248,250,252,0.9)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Preghiera {row.slot}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                          <InputLabel id={`reading-turn-${row.slot}-label`}>Ragazzo</InputLabel>
                          <Select
                            labelId={`reading-turn-${row.slot}-label`}
                            label="Ragazzo"
                            value={row.studentId || ''}
                            onChange={(e) => handleChangeReadingTurnStudent(row.slot, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Nessuno</em>
                            </MenuItem>
                            {students.map((s) => (
                              <MenuItem key={s.id} value={s.id}>
                                {s.cognome} {s.nome}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Testo della preghiera"
                          value={row.prayerText}
                          onChange={(e) => handleChangeReadingTurnPrayer(row.slot, e.target.value)}
                          multiline
                          minRows={2}
                          fullWidth
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 1 }}>
                <Button
                  variant="outlined"
                  onClick={handleExportReadingTurnsPdf}
                  disabled={readingTurnsLoading || readingTurnsSaving}
                >
                  Esporta in PDF
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSaveReadingTurns}
                  disabled={readingTurnsSaving}
                >
                  {readingTurnsSaving ? 'Salvataggio in corso…' : 'Salva turni preghiere'}
                </Button>
              </Box>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Registro delle letture recenti
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Storico sintetico di chi ha letto le preghiere, con data e testo affidato.
              </Typography>
              {readingTurnsLogLoading ? (
                <Box sx={{ py: 2 }}>
                  <LinearProgress />
                </Box>
              ) : (
                readingTurnsLog && readingTurnsLog.length ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Data</TableCell>
                        <TableCell>Ragazzo</TableCell>
                        <TableCell>Preghiera</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {readingTurnsLog.map((entry, index) => {
                        let dateLabel = '';
                        if (entry.meetingDate) {
                          const m = moment(entry.meetingDate, ['YYYY-MM-DD', moment.ISO_8601], true);
                          if (m.isValid()) {
                            dateLabel = m.format('DD/MM/YYYY');
                          }
                        }
                        if (!dateLabel && entry.createdAt) {
                          let d = entry.createdAt;
                          if (d && typeof d.toDate === 'function') {
                            d = d.toDate();
                          }
                          const m2 = moment(d);
                          if (m2.isValid()) {
                            dateLabel = m2.format('DD/MM/YYYY');
                          }
                        }
                        const name = entry.studentName || '';
                        const prayerPreview = entry.prayerText
                          ? (entry.prayerText.length > 120
                            ? `${entry.prayerText.slice(0, 117)}…`
                            : entry.prayerText)
                          : '';
                        return (
                          <TableRow key={entry.id || index}>
                            <TableCell>{dateLabel || '-'}</TableCell>
                            <TableCell>{name || '-'}</TableCell>
                            <TableCell>{prayerPreview || '-'}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Nessuna lettura registrata finora. Quando salvi i turni di preghiera, qui comparirà lo storico.
                  </Typography>
                )
              )}
            </Box>

          </DialogContent>
          <DialogActions>
            <Button onClick={() => setToolsModalOpen(false)}>Chiudi</Button>
          </DialogActions>
        </Dialog>


        {/* Conferma unificata (post param) */}
        <Dialog open={confirmOpen} onClose={closeConfirm} fullWidth maxWidth="xs">
          <DialogTitle sx={{ textAlign: 'center' }}>
            {confirmPayload.title || 'Conferma Invio Report'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Saranno generati e caricati in <b>Archivio&nbsp;Reports</b> i seguenti file:
            </Typography>

            {/* Nomi file centrati e con word-wrap */}
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
              }}
            >
              {(confirmPayload.files || []).map((f, i) => (
                <Typography
                  key={i}
                  variant="body2"
                  component="div"
                  sx={{
                    textAlign: 'center',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    maxWidth: '100%',
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                    background: 'rgba(0,0,0,0.04)',
                    borderRadius: 1,
                    px: 1,
                    py: 0.5,
                  }}
                >
                  {f.name}
                </Typography>
              ))}
            </Box>

            <Typography variant="body2" sx={{ mt: 1.5, textAlign: 'center' }}>
              Confermi di procedere?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeConfirm}>Chiudi</Button>
            <Button variant="contained" onClick={confirmProceed}>Sì, procedi</Button>
          </DialogActions>
        </Dialog>

        {/* Avanzamento upload unificato */}
        <Dialog open={uploadDlgOpen} onClose={closeUploadDialog} fullWidth maxWidth="xs">
          <DialogTitle
            sx={{
              textAlign: 'center',
              px: 2,
            }}
          >
            {/* Titolo centrato e “wrappato” */}
            <Box
              component="span"
              sx={{
                display: 'block',
                wordBreak: 'break-word',
                whiteSpace: 'normal',
                maxWidth: '100%',
                mx: 'auto',
              }}
            >
              {uploadDlgTitle}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>{uploadDlgMsg}</Typography>
            <LinearProgress variant="determinate" value={uploadPct} sx={{ mt: 2 }} />
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 1 }}>
              {uploadPct}%
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeUploadDialog}>Chiudi</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog salvataggio programma */}
        <Dialog open={openSaveDialog} onClose={cancelSaveProgram}>
          <DialogTitle sx={{ textAlign: 'center' }}>Conferma Salvataggio Programma</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Vuoi salvare il programma del giorno{' '}
              <b>{meetingDate ? moment(meetingDate).format('DD/MM/YYYY') : '-'}</b>?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelSaveProgram}>Chiudi</Button>
            <Button variant="contained" onClick={confirmSaveProgram}>Conferma</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog elimina programma */}
        <Dialog open={openDeleteProgramDialog} onClose={cancelDeleteProgram}>
          <DialogTitle sx={{ textAlign: 'center' }}>Conferma Eliminazione Programma</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Eliminare il programma del{' '}
              <b>
                {selectedProgramId
                  ? moment(savedPrograms.find((p) => p.id === selectedProgramId)?.meetingDate).format('DD/MM/YYYY')
                  : '-'}
              </b>
              ?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDeleteProgram}>Chiudi</Button>
            <Button variant="contained" color="error" onClick={confirmDeleteProgram}>Elimina</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog reset campi e presenze */}
        <Dialog open={openResetDialog} onClose={closeResetDialogHandler}>
          <DialogTitle sx={{ textAlign: 'center' }}>Reset campi e presenze</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Questa azione azzererà i nomi dei catechisti, la data dell&apos;incontro,
              il programma dell&apos;incontro del giorno e tutte le presenze degli studenti.
              Potrai comunque annullare l&apos;ultimo reset finché non chiudi o aggiorni la pagina.
              Sei sicuro di voler procedere?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={closeResetDialogHandler}>Annulla</Button>
            <Button variant="contained" color="error" onClick={confirmResetDashboard}>
              Sì, resetta tutto
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog conflitto */}
        <Dialog open={openConflictDialog} onClose={() => setOpenConflictDialog(false)}>
          <DialogTitle sx={{ textAlign: 'center' }}>Conflitto Programma</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
              Esiste già un programma per la data <b>{meetingDate ? moment(meetingDate).format('DD/MM/YYYY') : '-'}</b>.
              Vuoi <b>sovrascriverlo</b> o <b>rinominarlo</b> su un’altra data?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConflictDialog(false)}>Chiudi</Button>
            <Button onClick={handleOverwriteProgram}>Sovrascrivi</Button>
            <Button variant="contained" onClick={handleRenameProgram}>Rinomina</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog rinomina */}
        <Dialog open={openRenameDialog} onClose={cancelRenameProgram}>
          <DialogTitle sx={{ textAlign: 'center' }}>Rinomina Programma</DialogTitle>
          <DialogContent>
            <Typography variant="body2" gutterBottom sx={{ textAlign: 'center' }}>Inserisci una nuova data:</Typography>
            <TextField
              autoFocus
              margin="dense"
              type="date"
              fullWidth
              variant="outlined"
              value={newMeetingDate}
              onChange={(e) => setNewMeetingDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelRenameProgram}>Chiudi</Button>
            <Button variant="contained" onClick={confirmRenameProgram}>Conferma</Button>
          </DialogActions>
        </Dialog>

        {/* Form studenti */}
        {addOpen && (
          <StudentForm
            onClose={() => setAddOpen(false)}
            open={addOpen}
            onAddStudent={addBirthdayEvent}
          />
        )}
        {editOpen && selectedStudent && (
          <StudentEditForm
            student={selectedStudent}
            onClose={() => setEditOpen(false)}
            open={editOpen}
            onUpdateStudent={updateBirthdayEvent}
          />
        )}
        {detailsOpen && selectedStudent && (
          <StudentDetails
            student={selectedStudent}
            onClose={() => setDetailsOpen(false)}
            open={detailsOpen}
          />
        )}
      </div>

      {/* Snackbar uniforme */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2800}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={closeSnack} severity={snack.type} variant="filled" sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
</Snackbar>

{/* Alert studenti assenti da oltre 3 settimane */}
<Dialog
  open={absentAlertOpen}
  onClose={() => setAbsentAlertOpen(false)}
  maxWidth="sm"
  fullWidth
>
  <DialogTitle sx={{ textAlign: 'center' }}>
    Bambini che non vediamo da un po'
  </DialogTitle>
  <DialogContent>
    {longAbsenceStudents.length ? (
      <>
        <Typography variant="body2">
          I seguenti bambini non partecipano agli incontri di catechismo
          da più di 3 settimane. Può essere una buona occasione per un messaggino, un 
          saluto o un piccolo contatto di vicinanza con le famiglie:
        </Typography>
        <Box component="ul" sx={{ pl: 3, mt: 1 }}>
          {longAbsenceStudents.map((s) => (
            <li key={s.id}>
              {s.cognome} {s.nome}
            </li>
          ))}
        </Box>
        <Typography variant="body2" sx={{ mt: 1 }}>
          E' un piccolo promemoria per prenderci cura di chi,
          per qualsiasi motivo, si è allontanato per un po'.
        </Typography>
      </>
    ) : (
      <Typography variant="body2">
        Al momento non ci sono bambini che risultano assenti da più di 3 settimane.
      </Typography>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setAbsentAlertOpen(false)}>Chiudi</Button>
  </DialogActions>
</Dialog>

{/* Modale: nuovi appuntamenti pubblici */}
      <Dialog open={calendarAlertOpen} onClose={() => setCalendarAlertOpen(false)}>
        <DialogTitle>Nuovi appuntamenti in calendario</DialogTitle>
        <DialogContent>
          <Typography>
            Ci sono <strong>{calendarAlertCount}</strong> appuntamenti pubblici imminenti. Vuoi controllare il calendario?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCalendarAlertOpen(false)}>Chiudi</Button>
          <Button variant="contained" onClick={() => { setCalendarAlertOpen(false); navigate('/CalendarPage'); }}>
            Apri Calendario
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Dashboard